import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableContainer,
  TableRow,
  Stack,
  Typography,
  TextField,
  SxProps,
  IconButton,
  FormControlLabel,
  Switch,
} from "@mui/material";
import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  setSelectedMetric,
  setSelectedMonth,
  setAllReviewed,
  setForecastStatus,
  setForecastNote,
  setSelectedTemplateType,
  setSelectedMonthData,
  setSelectedMonthForecastContentSharedUid,
  setSelectedSite,
} from "../../../../../features/forecastersSlice";
import {
  Link as RouterLink,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { NumericFormat } from "react-number-format";
import {
  selectUserToken,
  setDisplayErrorSnackBar,
} from "../../../../../features/userSlice";
import { forecastersEndPointLogic } from "../../../../../logic/AssetManagement/Forecasters/forecastersEndPointLogic";
import store from "../../../../../store";
import HistoryIcon from "@mui/icons-material/History";
import { Theme } from "@emotion/react";
import MetricsByMonthDialogue from "../ForeCasterInfo/MetricsByMonth/MetricsByMonth";
import ForecastSkeleton from "../CreateNewForeCast/ForecastSkeleton";
import { usePastForecast } from "../../../../../customHooks/usePastForecast";
import {
  formatPercentageNonRound,
  formatPercentageNonRoundUptoTwo,
} from "../../../../../utils/helpers";
import "../ForeCasterInfo/FTDinfo.css";
import { TableWithOffsetScrollbar } from "../ForeCasterInfo/TableWithOffsetScrollbar";
import { useRecalculateMonthlyData } from "../../../../../customHooks/useRecalculateMonthlyData";
import InfoCard from "../CreateNewForeCast/InfoCard";
import { useForecastCopyDuplicate } from "../../../../../customHooks/Useforecastcopyduplicate";
import { GROWTH_CONFIG } from "../CreateNewForeCast/helpers/Forecastcopyhelper";
import {
  getGrowthFieldInfo,
  FIELD_NAMES,
} from "../CreateNewForeCast/helpers/GrowthConfig";
import QuarterSummaryCell from "../CreateNewForeCast/QuarterSummaryCell";

interface Props {
  selectedForecaster: any;
  loading: any;
  setLoading: any;
  onTableReady?: () => void;
  showContent?: boolean;
}

export const DuplicateForecastGrowth = ({
  selectedForecaster,
  loading,
  setLoading,
  onTableReady,
  showContent = false,
}: Props) => {
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const selectedTemplateType = useSelector(
    (state: any) => state?.forecasters?.selectedTemplateType,
  );
  const selectedYear = searchParams.get("selectedYear") as any;
  const forecastStatus = useSelector(
    (state: any) => state.forecasters.forecastStatus,
  );
  const selectedSite = searchParams.get("selectedSite") as any;
  const forecastNote = useSelector(
    (state: any) => state?.forecasters?.forcastNote,
  );
  const { lastMonthActiveUsersDuplicate } = useSelector(
    (state: any) => state.forecasters,
  );

  const monthNames = useMemo(
    () => [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ],
    [],
  );
  const [editableNRPU, setEditableNRPU] = useState<string | null>(null);
  const [editableFtd, setEditableFtd] = useState<string | null>(null);
  const [editableAvgNetREv, setEditableAvgNetREv] = useState<string | null>(
    null,
  );
  const [editableCPAPV, setEditableCPAPV] = useState<string | null>(null);
  const [editableCVR, setEditableCVR] = useState<string | null>(null);
  const [editableProfit, setEditableProfit] = useState<string | null>(null);
  const [editableChurnRate, setEditableChurnRate] = useState<string | null>(
    null,
  );
  const [editableAcquiredToFtd, setEditableAcquiredToFtd] = useState<
    string | null
  >(null);
  const [editableRsP, setEditableRsP] = useState<string | null>(null);
  const [editableRsBrNg, setEditableRsBrNg] = useState<string | null>(null);
  const [selectedFieldInfo, setSelectedFieldInfo] = useState<{
    fieldName: string;
    type: "Formula" | "User input" | "Parameter";
    valueSourceText: string;
    impactText: string;
  } | null>(null);

  // ── cell-click highlight state ──────────────────────────────────────
  const [selectedCell, setSelectedCell] = useState<{
    fieldName: string;
    month: string;
  } | null>(null);

  // Derive the set of formula field-names for the currently selected cell.
  const highlightedFieldNames = useMemo(() => {
    if (!selectedCell) return new Set<string>();
    const info = getGrowthFieldInfo(selectedCell.fieldName);
    if (!info?.formulaFields) return new Set<string>();

    const aliases: Record<string, string> = {
      "net rev": "estimated net revenue",
      rs: "estimated revenue share",
      "est. cpa income": "estimated cpa income",
      "cpa income": "estimated cpa income",
    };

    const formulaFieldsSet = new Set<string>();
    info.formulaFields.split(",").forEach((s: string) => {
      const trimmed = s.trim().toLowerCase();
      formulaFieldsSet.add(trimmed);
      if (aliases[trimmed]) {
        formulaFieldsSet.add(aliases[trimmed]);
      }
    });
    return formulaFieldsSet;
  }, [selectedCell]);

  // Returns true when fieldLabel is one of the formula source fields of the
  // currently-selected cell AND the row month matches.
  const isHighlighted = (fieldLabel: string, rowMonth: string) => {
    if (!selectedCell || selectedCell.month !== rowMonth) return false;
    return highlightedFieldNames.has(fieldLabel.toLowerCase());
  };

  const handleFieldClick = (fieldName: string) => {
    const fieldData = getGrowthFieldInfo(fieldName);
    if (!fieldData) {
      setSelectedFieldInfo(null);
      return;
    }

    // Build the value text (Formula or Glossary)
    const valueSourceText =
      fieldData.type === "Formula"
        ? `The formula for ${fieldName} is: ${fieldData.formula}` || ""
        : fieldData.glossaryText || "";

    // Build the impact text
    const impactText =
      fieldData.type === "Formula"
        ? fieldData.affectedFields
          ? `Changes in '${fieldName}', you will affect: ${fieldData.affectedFields}`
          : `Changes in '${fieldName}' will not affect other metrics directly.`
        : fieldData.affectedFields
          ? `By editing the '${fieldName}', you will affect: ${fieldData.affectedFields}`
          : `Changes in '${fieldName}' will not affect other metrics directly.`;

    setSelectedFieldInfo({
      fieldName,
      type: fieldData.type,
      valueSourceText,
      impactText,
    });
  };

  // Combined click handler: sets selectedCell AND calls the existing
  // handleFieldClick so the InfoCard still updates.
  const handleCellClick = (fieldName: string, month: string) => {
    setSelectedCell({ fieldName, month });
    handleFieldClick(fieldName);
  };
  const forecastLastTwelveMonthsData = useSelector(
    (state: any) => state?.forecasters?.forecastLastTwelveMonthsData,
  );
  const LastData = forecastLastTwelveMonthsData?.query_output || [];
  // const rrActiveUsersList = Math.round(
  //   LastData.filter((entry: any) => entry.month.includes("RR")).map(
  //     (entry: any) => entry.active_users
  //   )
  // );

  const tableheaders = useMemo(
    () => [
      { id: 0, KEY: "month", label: "Month", metric: "month", sortable: true },
      { id: 1, KEY: "nrpu", label: "NRPU", metric: "nrpu", sortable: true },
      { id: 2, KEY: "ftd", label: "FTD", metric: "ftd", sortable: true },
      {
        id: 3,
        KEY: "avg_net_rev_per_active_user",
        label: "Avg Net Rev To Active Users",
        metric: "avg_net_rev_per_active_user",
        sortable: true,
      },
      {
        id: 4,
        KEY: "cpa_pv",
        label: "CPA PV",
        metric: "cpa_pv",
        sortable: true,
      },
      { id: 5, KEY: "cvr", label: "CVR", metric: "CVR", sortable: true },
      {
        id: 6,
        KEY: "profit",
        label: "Profit",
        metric: "profit",
        sortable: true,
      },
      {
        id: 7,
        KEY: "active_users",
        label: "Active Users",
        metric: "active_users",
        sortable: true,
      },
      {
        id: 8,
        KEY: "old_active_users",
        label: "Old Active Users",
        metric: "old",
        sortable: true,
      },
      {
        id: 9,
        KEY: "acquired_active_users",
        label: "Acquired Users",
        metric: "aquired",
        sortable: true,
      },
      {
        id: 10,
        KEY: "growth_target",
        label: "Growth Target",
        metric: "growth",
        sortable: true,
      },
      {
        id: 11,
        KEY: "net_revenue",
        label: "Net Revenue",
        metric: "net_rev",
        sortable: true,
      },
      {
        id: 12,
        KEY: "rs",
        label: "RS",
        metric: "rs_income_dollar",
        sortable: true,
      },
      { id: 13, KEY: "rs_pv", label: "RS PV", metric: "rs_pv", sortable: true },
      {
        id: 14,
        KEY: "cpa",
        label: "CPA Income",
        metric: "cpa_income_dollar",
        sortable: true,
      },
      {
        id: 15,
        KEY: "total_income",
        label: "Total Income",
        metric: "total_income",
        sortable: true,
      },
      {
        id: 16,
        KEY: "clicks",
        label: "Clicks",
        metric: "clicks",
        sortable: true,
      },
      {
        id: 17,
        KEY: "epc_cpa",
        label: "EPC CPA",
        metric: "EPC_CPA",
        sortable: true,
      },
      {
        id: 18,
        KEY: "epc_rs",
        label: "EPC RS",
        metric: "EPC_RS",
        sortable: true,
      },
      { id: 19, KEY: "epc", label: "EPC", metric: "EPC", sortable: true },
      { id: 20, KEY: "cpc", label: "CPC", metric: "CPC", sortable: true },
      { id: 21, KEY: "cac", label: "CAC", metric: "CAC", sortable: true },
      { id: 22, KEY: "spend", label: "Spend", metric: "spend", sortable: true },
      {
        id: 23,
        KEY: "avg_rs_deal_p",
        label: "Avg RS Percent",
        metric: "avg_rs_p",
        sortable: true,
      },
      {
        id: 24,
        KEY: "churn_rate",
        label: "Churn Rate",
        metric: "churn_rate",
        sortable: true,
      },
      {
        id: 25,
        KEY: "br_to_ng_active_rs_ratio",
        label: "RS BR to NG Active Percent",
        metric: "rs_br_ng",
        sortable: true,
      },
      {
        id: 26,
        KEY: "ratio_between_acquired_to_ftd",
        label: "Acquired to FTD",
        metric: "Ratio_between_acquired_to_ftd",
        sortable: true,
      },
    ],
    [],
  );

  const sortedRows = selectedForecaster?.forecaster_content || 0;
  // const getQuarterStartDate = (month: number, year: number) => {
  //   const quarterStartMonth = Math.floor(month / 3) * 3; // Calculate the first month of the quarter
  //   return new Date(year, quarterStartMonth, 15); // Return the 15th of the first month of the quarter
  // };

  const isMonthPassed = (rowMonth: string) => {
    const [year, month] = rowMonth.split("-").map(Number);
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();

    // If viewing past year's forecast, all months are considered passed
    if (year < currentYear) {
      return true;
    }

    // If viewing future year's forecast, no months are considered passed
    if (year > currentYear) {
      return false;
    }

    // For current year, compare with current date
    const monthEndDate = new Date(year, month, 0);
    return currentDate > monthEndDate;
  };

  const modifiedRows = sortedRows?.map((row: any) => {
    const [, month] = row?.month.split("-").map(Number);
    const monthName = monthNames[month - 1]; // Get month name for display
    return { ...row, displayMonth: monthName }; // Add displayMonth for UI
  });

  // Sort the modifiedRows by month before setting state
  const sortedModifiedRows = modifiedRows.sort((a: any, b: any) => {
    const monthIndexA = monthNames.indexOf(a.displayMonth);
    const monthIndexB = monthNames.indexOf(b.displayMonth);
    return monthIndexA - monthIndexB;
  });

  const editableRows = sortedModifiedRows.map((row: any) => ({ ...row }));

  const formatNumber = (value: number | string) => {
    if (typeof value === "number") {
      return new Intl.NumberFormat("en-US", {
        maximumFractionDigits: 0,
        useGrouping: true,
      }).format(value);
    }
    return value;
  };

  const calculateInitialValues = (month: string, editableRows: any[]) => {
    const rowData = editableRows.find((row: any) => row.displayMonth === month);
    if (!rowData) return null;

    return {
      growth: parseFloat(rowData.growth_target),
      cpaPv: parseFloat(rowData.cpa_pv),
      avgNetRevPerActiveUser: parseFloat(rowData.avg_net_rev_per_active_user),
      churnRate: parseFloat(rowData.churn_rate),
      ratioBetweenAcquiredToFtd: parseFloat(
        rowData.ratio_between_acquired_to_ftd,
      ),
      avgRsP: parseFloat(rowData.avg_rs_deal_p),
      rsBrToRsActiveRatio: parseFloat(rowData.br_to_ng_active_rs_ratio),
      cvr: parseFloat(rowData.cvr),
      profit: parseFloat(rowData.profit),
      ftd: parseFloat(rowData.ftd),
      oldActiveUsers: parseFloat(rowData.old_active_users),
      acquiredUsers: parseFloat(rowData.acquired_active_users),
      rsPv: parseFloat(rowData.rs_pv),
      estRs: parseFloat(rowData.rs),
      estCpaIncome: parseFloat(rowData.cpa),
      clicks: parseFloat(rowData.clicks),
      cac: parseFloat(rowData.cac),
      spend: parseFloat(rowData.spend),
      totalIncome: parseFloat(rowData.total_income),
      epcCpa: parseFloat(rowData.epc_cpa),
      epcRs: parseFloat(rowData.epc_rs),
      epc: parseFloat(rowData.epc),
      cpc: parseFloat(rowData.cpc),
      activeUsers: parseFloat(rowData.active_users),
      estNetRev: parseFloat(rowData.net_revenue),
      nrpu: parseFloat(rowData.nrpu) || 0,
    };
  };

  const [monthlyData, setMonthlyData] = useState(() => {
    return editableRows.reduce(
      (acc: any, row: any) => ({
        ...acc,
        [row.displayMonth]: {
          ...calculateInitialValues(row.displayMonth, editableRows),
          displayMonth: row.displayMonth,
          content_shared_uid: row?.content_shared_uid,
          month: row.month,
        },
      }),
      {},
    );
  });

  const calculateValues = (
    month: string,
    newValues: any,
    forcePreviousMonthActiveUsers?: number,
  ) => {
    const data = { ...monthlyData[month], ...newValues };
    let previousMonthActiveUsers;
    const monthIndex = monthNames.indexOf(month);
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth();
    // const currentYear = currentDate.getFullYear();
    const currentDay = currentDate.getDate();
    const selectedYearNum = Number(selectedYear);

    // If forcePreviousMonthActiveUsers is provided, use that value
    if (forcePreviousMonthActiveUsers !== undefined) {
      previousMonthActiveUsers = forcePreviousMonthActiveUsers;
    } else {
      // Determine if we're in early Q3 (July 1-15)
      const isEarlyQ3 = currentMonth === 6 && currentDay <= 15;

      // Check if the month is passed according to isMonthPassed
      const monthNum = monthIndex + 1;
      const paddedMonth = monthNum.toString().padStart(2, "0");
      const monthStr = `${selectedYear}-${paddedMonth}`;
      const isPassed = isMonthPassed(monthStr);
      if (isPassed) {
        // For passed months, use the RR data
        previousMonthActiveUsers = lastMonthActiveUsersDuplicate;
      } else {
        // For future months, use cascading calculation
        if (monthIndex === 0) {
          // January is the first month, use RR data
          previousMonthActiveUsers = lastMonthActiveUsersDuplicate;
        } else {
          // For other months, use the previous month's calculated active_users
          const prevMonthName = monthNames[monthIndex - 1];

          // Check if previous month is passed or already calculated
          const prevMonthStr = `${selectedYear}-${monthIndex
            .toString()
            .padStart(2, "0")}`;
          const isPrevMonthPassed = isMonthPassed(prevMonthStr);

          if (isPrevMonthPassed) {
            // If previous month is passed, use data from LastYearData
            // For Scenario 2: If we're in early Q3 (July 1-15), use June's data from last 12 month forecast
            if (isEarlyQ3 && monthIndex === 6) {
              // July
              const juneData = LastData.find((entry: any) => {
                const [year, month] = entry.month.split("-").map(Number);
                return month === 6 && year === selectedYearNum; // June of selected year
              });
              previousMonthActiveUsers =
                juneData?.active_users || lastMonthActiveUsersDuplicate;
            } else {
              previousMonthActiveUsers = lastMonthActiveUsersDuplicate;
            }
          } else if (monthlyData[prevMonthName]) {
            // If previous month is already calculated, use its active_users
            previousMonthActiveUsers = monthlyData[prevMonthName].activeUsers;
          } else {
            // Fallback to RR data if previous month data is not available
            previousMonthActiveUsers = lastMonthActiveUsersDuplicate;
          }
        }
      }
    }
    // Always recalculate oldActiveUsers based on the provided or calculated previousMonthActiveUsers
    data.oldActiveUsers = Math.round(
      (1 - data.churnRate) * Math.round(previousMonthActiveUsers),
    );

    // if (selectedTemplateType === 2 || selectedTemplateType === 3) {
    // For templates 2 and 3, FTD is fixed and growth is calculated
    data.growth =
      (data.oldActiveUsers + data.acquiredUsers) /
        Math.round(previousMonthActiveUsers) -
      1;
    // } else {
    //   // For template 1, growth is fixed and FTD is calculated
    //   data.ftd = Math.round(
    //     ((1 / (1 - data.churnRate) - 1) * data.oldActiveUsers +
    //       data.growth * Math.round(previousMonthActiveUsers)) /
    //       data.ratioBetweenAcquiredToFtd
    //   );
    // }

    // Recalculate all dependent values
    data.acquiredUsers = data.ratioBetweenAcquiredToFtd * data.ftd;
    data.activeUsers = data.oldActiveUsers + data.acquiredUsers;

    // Ensure growth is consistent with activeUsers change for all templates
    if (selectedTemplateType === 2 || selectedTemplateType === 3) {
      data.growth = data.activeUsers / Math.round(previousMonthActiveUsers) - 1;
    }

    data.estNetRev =
      data.avgNetRevPerActiveUser * data.activeUsers * data.rsBrToRsActiveRatio;
    data.estRs = data.estNetRev * data.avgRsP;
    data.rsPv = data.estRs / data.ftd;
    data.estCpaIncome = data.cpaPv * data.ftd;
    data.totalIncome = data.estCpaIncome + data.estRs;
    data.clicks = Math.round(data.ftd / data.cvr);
    // if (selectedTemplateType === 1 || selectedTemplateType === 2) {
    // For templates 1 and 2, profit is fixed and spend/cac are calculated
    data.spend = data.totalIncome - data.profit;
    data.cac = data.spend / data.ftd;
    // } else {
    //   // For template 3, CAC is fixed and profit/spend are calculated
    //   data.spend = data.cac * data.ftd;
    //   data.profit = data.totalIncome - data.spend;
    // }
    data.epcRs = data.estRs / data.clicks;
    data.epcCpa = data.estCpaIncome / data.clicks;
    data.epc = data.epcCpa + data.epcRs;
    data.cpc = data.spend / data.clicks;
    data.nrpu = newValues.nrpu !== undefined ? newValues.nrpu : data.nrpu || 0;
    return data;
  };

  useRecalculateMonthlyData({
    lastMonthActiveUsersDuplicate,
    selectedYear,
    monthlyData,
    monthNames,
    isMonthPassed,
    calculateValues,
    LastData,
    setMonthlyData,
  });

  const handleInputChange = (month: string, field: string, value: number) => {
    let updatedMonthlyData = { ...monthlyData }; // Start with the changed month
    const monthIndex = monthNames.indexOf(month);
    updatedMonthlyData[month] = calculateValues(month, { [field]: value }); // Update the current month first

    for (let i = monthIndex + 1; i < monthNames.length; i++) {
      // Recalculate all subsequent months
      const nextMonth = monthNames[i];
      if (updatedMonthlyData[nextMonth]) {
        // Only update if the month exists in monthlyData
        const prevMonth = monthNames[i - 1]; // Get the previous month and its active users
        const prevMonthActiveUsers = updatedMonthlyData[prevMonth].activeUsers;
        updatedMonthlyData[nextMonth] = calculateValues(
          nextMonth,
          updatedMonthlyData[nextMonth],
          prevMonthActiveUsers,
        ); // Recalculate with the forced previous month's active users
      }
    }
    setMonthlyData(updatedMonthlyData);
  };

  const onRatioAcquiredToFtdChange = (e: any, month: string) => {
    handleInputChange(
      month,
      "ratioBetweenAcquiredToFtd",
      parseFloat(e.target.value),
    );
  };

  const onAvgNetRevPerActiveUserChange = (e: any, month: string) => {
    handleInputChange(
      month,
      "avgNetRevPerActiveUser",
      parseFloat(e.target.value),
    );
  };

  const onCpaPvChange = (e: any, month: string) => {
    handleInputChange(month, "cpaPv", parseFloat(e.target.value));
  };

  const onCvrChange = (e: any, month: string) => {
    handleInputChange(month, "cvr", parseFloat(e.target.value));
  };

  const onProfitChange = (e: any, month: string) => {
    handleInputChange(month, "profit", parseFloat(e.target.value));
  };

  const onAvgRsPChange = (e: any, month: string) => {
    handleInputChange(month, "avgRsP", parseFloat(e.target.value));
  };

  const onRsBrToRsActiveRatio = (e: any, month: string) => {
    handleInputChange(month, "rsBrToRsActiveRatio", parseFloat(e.target.value));
  };

  const onChurnRateChange = (e: any, month: string) => {
    handleInputChange(month, "churnRate", parseFloat(e.target.value));
  };

  const onFtdChange = (e: any, month: string) => {
    handleInputChange(month, "ftd", parseFloat(e.target.value));
  };

  const onNrpuChange = (e: any, month: string) => {
    handleInputChange(month, "nrpu", parseFloat(e.target.value) || 0);
  };

  const isMonthEditable = (monthName: string) => {
    const currentDate = new Date();
    const monthIndex = monthNames.indexOf(monthName);
    const currentYear = currentDate.getFullYear();

    // If selected year is future year, all months are editable
    if (Number(selectedYear) > currentYear) {
      return true;
    }

    // If selected year is past year, no months are editable
    if (Number(selectedYear) < currentYear) {
      return false;
    }

    // For current year, apply the quarter logic
    const currentQuarter = Math.floor(
      currentDate.getDate() > 15
        ? currentDate.getMonth() / 3
        : (currentDate.getMonth() - 1) / 3,
    );

    // Block all forecasts if we're in Q4 after 15th Oct of the selected year
    if (currentDate.getMonth() >= 9 && currentDate.getDate() > 15) {
      return false;
    }

    const lockedMonths = (currentQuarter + 1) * 3;
    return monthIndex >= lockedMonths;
  };

  const handleCreateForecast = async (status: "draft" | "publish") => {
    try {
      setLoading(true);
      const forecastData = Object.entries(monthlyData)
        .filter(([month]) => isMonthEditable(month))
        .map(([month, data]) => {
          const monthIndex = monthNames.indexOf(month) + 1;
          const paddedMonth = monthIndex.toString().padStart(2, "0");
          const typedData = data as any;
          return {
            month: `${selectedYear}-${paddedMonth}-01`,
            spend: typedData.spend,
            clicks: typedData.clicks,
            cpc: typedData.cpc,
            ftd: Number(typedData.ftd.toFixed(0)),
            cvr: Number(typedData.ftd.toFixed(0)) / typedData.clicks,
            cac: typedData.cac,
            profit: typedData.profit,
            cpa: typedData.estCpaIncome,
            rs: typedData.estRs,
            total_income: typedData.totalIncome,
            cpa_pv: typedData.cpaPv,
            rs_pv: typedData.rsPv,
            pv: typedData.cpaPv + typedData.rsPv,
            growth_target: typedData.growth,
            active_users: typedData.activeUsers,
            old_active_users: typedData.oldActiveUsers,
            acquired_active_users: typedData.acquiredUsers,
            churn_rate: typedData.churnRate,
            ratio_between_acquired_to_ftd: typedData.ratioBetweenAcquiredToFtd,
            avg_net_rev_per_active_user: typedData.avgNetRevPerActiveUser,
            net_revenue: typedData.estNetRev,
            avg_rs_deal_p: typedData.avgRsP,
            br_to_ng_active_rs_ratio: typedData.rsBrToRsActiveRatio,
            epc_cpa: typedData.epcCpa,
            epc_rs: typedData.epcRs,
            epc: typedData.epc,
            nrpu: typedData.nrpu || 0,
          };
        });
      const currentDate = new Date(); // Block forecast creation if in Q4 after 15th Oct
      const currentYear = currentDate.getFullYear();
      if (
        Number(selectedYear) === currentYear &&
        ((currentDate.getMonth() === 9 && currentDate.getDate() > 15) ||
          currentDate.getMonth() > 9)
      ) {
        setLoading(false);
        store.dispatch(
          setDisplayErrorSnackBar({
            message:
              "Forecast creation for Q4 is closed after 15th October. You can no longer create or modify forecasts for this period.",
            type: "info",
          }),
        );
        return;
      }
      if (!oauthTokenHeader) {
        throw new Error("User doesn't have permissions to create new forecast");
      }
      const response = await forecastersEndPointLogic.createNewForecast(
        oauthTokenHeader,
        {
          year: Number(selectedYear),
          template_type: selectedTemplateType,
          last_month_active_users: Number(lastMonthActiveUsersDuplicate),
          status: status,
          notes: forecastNote,
          forecaster_content: forecastData,
        },
        selectedSite,
      );
      if (response?.status === 201) {
        // Update the forecasters list in the background
        await forecastersEndPointLogic.getForecastersList(
          selectedSite,
          oauthTokenHeader,
          selectedYear,
          dispatch,
        );
        // Reset all states
        dispatch(setForecastStatus(null));
        dispatch(setForecastNote(""));
        dispatch(setAllReviewed(false));
        dispatch(setSelectedTemplateType(null));
        // Show success message and navigate
        store.dispatch(
          setDisplayErrorSnackBar({
            message: "Forecast Has Been Created Successfully",
            type: "success",
          }),
        );
        navigate(
          `/business_leader/forecaster?${new URLSearchParams(
            location.search,
          ).toString()}`,
        );
      }
    } catch (error: any) {
      store.dispatch(
        setDisplayErrorSnackBar({
          message: error?.message || "Error creating forecast",
          type: "error",
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (forecastStatus) {
      handleCreateForecast(forecastStatus);
      dispatch(setForecastStatus(null));
    }
    // eslint-disable-next-line
  }, [forecastStatus]);

  const handleInfoIconClick = (row: any) => {
    dispatch(setSelectedMonthData(row));
    dispatch(setSelectedMonthForecastContentSharedUid(row?.content_shared_uid));
  };

  const [openMetrics, setOpenMetrics] = useState(false); // used for opening history popup

  const handleClickOpen = (metricKey: string) => {
    setOpenMetrics(true);
    dispatch(setSelectedMetric(metricKey));
    const currentMonth = new Date().getMonth(); // 0–11
    const month = String(currentMonth + 1).padStart(2, "0");
    const formattedMonth = `${selectedYear}-${month}-01`;
    dispatch(setSelectedMonth(formattedMonth));
    if (selectedSite) {
      dispatch(setSelectedSite(selectedSite));
    } else {
      store.dispatch(
        setDisplayErrorSnackBar(
          "Please select a site.No selected site found in the URL",
        ),
      );
    }
  };

  const [showOnlyForecasts, setShowOnlyForecasts] = useState(false);

  const handleToggleDisplayForecasts = (event: any) => {
    setShowOnlyForecasts(event.target.checked);
    // Force recalc of sticky offsets (triggers your useLayoutEffect)
    setTimeout(() => {
      window.dispatchEvent(new Event("resize"));
    }, 0);
  };

  const { forecastData } = usePastForecast(
    modifiedRows,
    oauthTokenHeader,
    selectedSite,
  );

  const CleanedHistoryData = forecastData.map((data: any) => {
    const cleanedItems = Object.fromEntries(
      Object.entries(data).filter(
        ([key]) =>
          !["input_params", "meta_data", "output_metadata"].includes(key),
      ),
    );
    return cleanedItems;
  });

  const getDeviation = (month: string, metricName: string) => {
    //  Check if the month has passed — only then include it
    if (getMonthIndicator(month) !== "A") return null;

    //  Find only those entries in CleanedHistoryData where the month has passed
    const monthData = CleanedHistoryData?.find(
      (item: any) => item.month === month,
    );

    if (!monthData) return null;

    const metricItem = monthData.query_output?.find(
      (m: any) => m.Metric.toLowerCase() === metricName.toLowerCase(),
    );

    return metricItem?.Deviation ?? null;
  };

  const getActual = (month: string, metricName: string) => {
    // Check if the month has passed — only then include it
    if (getMonthIndicator(month) !== "A") return null;

    // Find only those entries in CleanedHistoryData where the month has passed
    const monthData = CleanedHistoryData?.find(
      (item: any) => item.month === month,
    );

    if (!monthData) return null;

    const metricItem = monthData.query_output?.find(
      (m: any) => m.Metric.toLowerCase() === metricName.toLowerCase(),
    );

    return metricItem?.Actual ?? null;
  };

  const handleClose = () => {
    setOpenMetrics(false);
  };

  //(A) for months that have passed
  //(R) for the first month of the current quarter (if not passed)
  //(F) for all other future months
  const getMonthIndicator = (rowMonth: string) => {
    const [year, month] = rowMonth.split("-").map(Number);
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1; // JS months are 0-based
    const currentDay = currentDate.getDate();

    // Case 1: If the month has already passed
    if (isMonthPassed(rowMonth)) {
      return "A";
    }

    // Case 2: If it's the current month
    if (year === currentYear && month === currentMonth) {
      return currentDay <= 15 ? "R" : "A"; // before or on 15th = R, after = A
    }

    // Case 3: Future months
    return "F";
  };

  // Sticky header logic
  const leftStickyIds = useMemo(
    () => new Set<number>([0, 1, 2, 3, 4, 5, 6]),
    [],
  );
  const rightStickyIds = useMemo(() => new Set<number>([]), []);

  const headerCellRefs = useRef<Array<HTMLTableCellElement | null>>([]);
  const assignedCountRef = useRef(0);
  // whenever header count changes, ensure the ref array has that length
  useEffect(() => {
    headerCellRefs.current = Array(tableheaders.length).fill(null);
    assignedCountRef.current = 0;
  }, [tableheaders.length]);

  // state to hold computed offsets for left and right sticky cells
  const [computedLeftOffsets, setComputedLeftOffsets] = useState<
    Record<number, number>
  >({});
  const [computedRightOffsets, setComputedRightOffsets] = useState<
    Record<number, number>
  >({});

  // callbackRef factory — used as ref prop on TableCell
  const makeHeaderRef =
    (index: number) => (el: HTMLTableCellElement | null) => {
      // assign element (can be null when unmounting)
      const prev = headerCellRefs.current[index];
      headerCellRefs.current[index] = el;

      // if element newly attached, increment assigned count
      if (!prev && el) {
        assignedCountRef.current++;
      }

      // if element removed, decrement (keeps count accurate on re-renders)
      if (prev && !el) {
        assignedCountRef.current = Math.max(0, assignedCountRef.current - 1);
      }
    };

  // shallow comparison helper — only compare own-number-valued props (fast)
  const offsetsEqual = (
    a: Record<number, number>,
    b: Record<number, number>,
  ) => {
    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);
    if (aKeys.length !== bKeys.length) return false;
    for (const k of aKeys) {
      if ((a as any)[k] !== (b as any)[k]) return false;
    }
    return true;
  };

  // measure widths and compute cumulative left/right offsets
  useLayoutEffect(() => {
    let mounted = true;
    let ro: ResizeObserver | null = null;

    const computeOffsets = () => {
      if (!mounted) return;
      const leftOffsets: Record<number, number> = {};
      let cumLeft = 0;
      for (let i = 0; i < tableheaders.length; i++) {
        if (leftStickyIds.has(i)) {
          leftOffsets[i] = cumLeft;
          const el = headerCellRefs.current[i];
          const w = el ? el.getBoundingClientRect().width : i === 0 ? 115 : 0;
          cumLeft += w;
        }
      }

      const rightOffsets: Record<number, number> = {};
      let cumRight = 0;
      const rightIds = Array.from(rightStickyIds).sort((a, b) => b - a);
      for (const id of rightIds) {
        rightOffsets[id] = cumRight;
        const el = headerCellRefs.current[id];
        const w = el ? el.getBoundingClientRect().width : 115;
        cumRight += w;
      }

      setComputedLeftOffsets((prev) => {
        if (!mounted) return prev;
        if (offsetsEqual(prev || {}, leftOffsets)) return prev;
        return leftOffsets;
      });

      setComputedRightOffsets((prev) => {
        if (!mounted) return prev;
        if (offsetsEqual(prev || {}, rightOffsets)) return prev;
        return rightOffsets;
      });
    };

    const scheduleFinalMeasure = () => {
      // prefer fonts.ready when available, then double-RaF to ensure layout is final
      if ((document as any).fonts && (document as any).fonts.ready) {
        (document as any).fonts.ready.then(() => {
          requestAnimationFrame(() =>
            requestAnimationFrame(() => mounted && computeOffsets()),
          );
        });
      } else {
        requestAnimationFrame(() =>
          requestAnimationFrame(() => mounted && computeOffsets()),
        );
      }
    };

    // If header refs aren't all assigned yet, wait for them.
    // Otherwise run immediate measurement (and schedule the stable re-measure).
    const tryInitialMeasure = () => {
      const assigned = assignedCountRef.current;
      if (assigned < tableheaders.length) {
        // not all refs attached yet — defer until they are attached.
        // Poll briefly until assignedCount matches the header count (fast and stops rapidly).
        const check = () => {
          if (!mounted) return;
          if (assignedCountRef.current >= tableheaders.length) {
            computeOffsets(); // one immediate compute
            scheduleFinalMeasure();
          } else {
            // check again next frame (small, bounded polling)
            requestAnimationFrame(check);
          }
        };
        requestAnimationFrame(check);
      } else {
        // all refs available right now
        computeOffsets();
        scheduleFinalMeasure();
      }
    };

    // Start: try to measure once refs are present
    tryInitialMeasure();

    // Keep ResizeObserver for future changes, schedule via rAF
    const tableEl = document.querySelector(".FTDForecasterTable table");
    if (tableEl) {
      ro = new ResizeObserver(() =>
        requestAnimationFrame(() => mounted && computeOffsets()),
      );
      ro.observe(tableEl);
    }
    window.addEventListener("resize", computeOffsets);

    return () => {
      mounted = false;
      if (ro) ro.disconnect();
      window.removeEventListener("resize", computeOffsets);
    };
  }, [tableheaders, leftStickyIds, rightStickyIds]);

  //for scrolling from 6th profit column
  const FIXED_LAST_INDEX = 6;

  const fixedColumnsWidth = useMemo(() => {
    const offset = computedLeftOffsets[FIXED_LAST_INDEX] ?? 0;
    const el = headerCellRefs.current[FIXED_LAST_INDEX];
    const width = el ? el.getBoundingClientRect().width : 0;
    return offset + width;
  }, [computedLeftOffsets]);

  const tableScrollRef = useRef<HTMLDivElement>(null);
  const editableStates = {
    editableNRPU,
    editableFtd,
    editableAvgNetREv,
    editableCPAPV,
    editableCVR,
    editableProfit,
    editableChurnRate,
    editableAcquiredToFtd,
    editableRsP,
    editableRsBrNg,
  };

  const isEditing =
    editableNRPU !== null ||
    editableFtd !== null ||
    editableAvgNetREv !== null ||
    editableCPAPV !== null ||
    editableCVR !== null ||
    editableProfit !== null ||
    editableChurnRate !== null ||
    editableAcquiredToFtd !== null ||
    editableRsP !== null ||
    editableRsBrNg !== null;

  const { handleCopyToNext } = useForecastCopyDuplicate({
    config: GROWTH_CONFIG,
    editableStates,
    monthlyData: monthlyData, // This is the local state variable
    setMonthlyData: setMonthlyData, // This is the local setter function
  });

  useEffect(() => {
    if (forecastData.length > 0 && onTableReady) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          onTableReady();
        });
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forecastData.length]);

  return (
    <>
      {!loading && forecastData.length > 0 ? (
        showContent ? (
        <>
          <Paper elevation={0} sx={{ boxShadow: "none", borderRadius: "none" }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "flex-end",
                padding: "8px 16px",
                position: "absolute",
                top: 66,
                right: 0,
              }}
            >
              <Box>
                <FormControlLabel
                  control={
                    <Switch
                      color="secondary"
                      checked={showOnlyForecasts}
                      onChange={handleToggleDisplayForecasts}
                    />
                  }
                  label="Show only forecasts"
                  labelPlacement="start"
                />
              </Box>
            </Box>
            <>
              <InfoCard
                selectedFieldName={selectedFieldInfo?.fieldName}
                fieldType={selectedFieldInfo?.type}
                valueSourceText={selectedFieldInfo?.valueSourceText}
                impactText={selectedFieldInfo?.impactText}
                isEditing={isEditing}
                editableStates={editableStates}
                config={GROWTH_CONFIG}
                onCopyToNext={handleCopyToNext}
              />
              <TableContainer
                className="FTDForecasterTable"
                ref={tableScrollRef}
              >
                <Table aria-label="collapsible table" size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {tableheaders.map((header) => {
                        const getCellSx = (
                          headerId: number,
                        ): SxProps<Theme> => {
                          const base: SxProps<Theme> = {
                            color: "#000000",
                            padding:
                              headerId === 0
                                ? "0px 8px 0px 16px!important"
                                : "0px 8px !important",
                            boxSizing: "border-box",
                            backgroundColor: "#fff",
                            top: 0,
                            borderCollapse: "separate",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          };

                          if (leftStickyIds.has(headerId)) {
                            return {
                              ...base,
                              position: "sticky",
                              left: computedLeftOffsets[headerId] ?? 0,
                              zIndex: 99,
                              backgroundColor: "#ffffff",
                              borderRight:
                                headerId === 6
                                  ? "1px solid #e0e0e0 !important"
                                  : undefined,
                              color: headerId === 0 ? "#000000" : "#9C27B0",
                            };
                          }

                          if (rightStickyIds.has(headerId)) {
                            return {
                              ...base,
                              position: "sticky",
                              right: computedRightOffsets[headerId],
                              zIndex: 110,
                              backgroundColor: "#ffffff",
                              // borderLeft:
                              //   headerId === 23
                              //     ? "1px solid #e0e0e0"
                              //     : undefined,
                            };
                          }

                          return base;
                        };

                        return (
                          <TableCell
                            key={header.id}
                            align="left"
                            sx={getCellSx(header.id)}
                            ref={makeHeaderRef(header.id)}
                          >
                            <Stack
                              direction="row"
                              alignItems="center"
                              justifyContent="flex-start"
                              spacing={1}
                            >
                              {header.label !== "Month" &&
                              header.label !== "More" ? (
                                <IconButton
                                  color="secondary"
                                  size="small"
                                  data-tracker="131"
                                  onClick={() => handleClickOpen(header.metric)}
                                >
                                  <HistoryIcon
                                    sx={{ fontSize: "1rem" }}
                                    color="secondary"
                                  />
                                </IconButton>
                              ) : null}
                              {
                                <Typography
                                  sx={{
                                    fontWeight: 500,
                                    fontSize: "14px",
                                    lineHeight: "24px",
                                    letterSpacing: "0.17px",
                                  }}
                                >
                                  {header.label}
                                </Typography>
                              }
                            </Stack>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.values(monthlyData).map((row: any, index) => {
                      const isQuarterEnd = [
                        "March",
                        "June",
                        "September",
                        "December",
                      ].includes(row.displayMonth);

                      // Calculate quarter totals if this is a quarter end
                      let quarterTotals: any = null;
                      if (isQuarterEnd) {
                        const allMonths = Object.values(monthlyData).filter(
                          (m: any) => m.displayMonth !== "placeholder",
                        );
                        const quarterMonths = allMonths.slice(
                          Math.max(0, index - 2),
                          index + 1,
                        );

                        quarterTotals = {
                          ftd: quarterMonths.reduce(
                            (sum: number, m: any) => sum + (m.ftd || 0),
                            0,
                          ),
                          profit: quarterMonths.reduce(
                            (sum: number, m: any) => sum + (m.profit || 0),
                            0,
                          ),
                          growth: quarterMonths.reduce(
                            (sum: number, m: any) => sum + (m.growth || 0),
                            0,
                          ),
                          estNetRev: quarterMonths.reduce(
                            (sum: number, m: any) => sum + (m.estNetRev || 0),
                            0,
                          ),
                          estRs: quarterMonths.reduce(
                            (sum: number, m: any) => sum + (m.estRs || 0),
                            0,
                          ),
                          estCpaIncome: quarterMonths.reduce(
                            (sum: number, m: any) =>
                              sum + (m.estCpaIncome || 0),
                            0,
                          ),
                          totalIncome: quarterMonths.reduce(
                            (sum: number, m: any) => sum + (m.totalIncome || 0),
                            0,
                          ),
                          spend: quarterMonths.reduce(
                            (sum: number, m: any) => sum + (m.spend || 0),
                            0,
                          ),
                          cac: quarterMonths.reduce(
                            (sum: number, m: any) => sum + (m.cac || 0),
                            0,
                          ),
                          clicks: quarterMonths.reduce(
                            (sum: number, m: any) => sum + (m.clicks || 0),
                            0,
                          ),
                        };
                      }
                      return (
                        <React.Fragment key={index}>
                          <TableRow
                            sx={{
                              backgroundColor: isMonthPassed(row.displayMonth)
                                ? "#EBEBEB"
                                : "inherit",
                              "& > *": {
                                borderBottom:
                                  "1px solid rgba(224, 224, 224, 1) !important",
                              },
                              height: 35,
                            }}
                          >
                            <TableCell
                              align="center"
                              sx={{
                                backgroundColor: "#FFFFFF",
                                padding: "0px 8px 0px 16px !important",
                                position: "sticky",
                                left: computedLeftOffsets[0] ?? 0,
                                zIndex: 2,
                              }}
                            >
                              <Stack
                                direction="row"
                                alignItems="center"
                                gap={0.5}
                              >
                                {getMonthIndicator(row.month) === "A" ? (
                                  <RouterLink
                                    to={`MonthlySummery?${(() => {
                                      const params = new URLSearchParams(
                                        searchParams,
                                      );
                                      params.set("page", "0");
                                      params.set(
                                        "contentSharedUid",
                                        row?.content_shared_uid,
                                      );
                                      params.set(
                                        "month",
                                        `${row?.displayMonth} ${selectedYear}`,
                                      );
                                      params.set("selectedMonth", row?.month);
                                      return params.toString();
                                    })()}`}
                                    onClick={() => handleInfoIconClick(row)}
                                    style={{
                                      textDecoration: "none",
                                      color: "inherit",
                                      display: "flex",
                                      gap: "4px",
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      color="secondary"
                                    >
                                      ({showOnlyForecasts ? "F" : "A"})
                                    </Typography>
                                    {row?.displayMonth ? (
                                      <Typography
                                        variant="body2"
                                        color="secondary"
                                        component="span"
                                        sx={{ textDecoration: "underline" }}
                                      >
                                        {row.displayMonth}
                                      </Typography>
                                    ) : (
                                      ""
                                    )}
                                  </RouterLink>
                                ) : (
                                  <>
                                    <Typography variant="body2">
                                      ({getMonthIndicator(row.month)})
                                    </Typography>
                                    {row?.displayMonth ? (
                                      <Typography
                                        variant="body2"
                                        component="span"
                                      >
                                        {row.displayMonth}
                                      </Typography>
                                    ) : (
                                      ""
                                    )}
                                  </>
                                )}
                              </Stack>
                            </TableCell>
                            <TableCell
                              align="center"
                              onClick={() =>
                                handleCellClick(
                                  FIELD_NAMES.NRPU,
                                  row.displayMonth,
                                )
                              }
                              sx={{
                                padding: "0px !important",
                                position: "sticky",
                                left: computedLeftOffsets[1],
                                zIndex: 3,
                                backgroundColor: isHighlighted(
                                  FIELD_NAMES.NRPU,
                                  row.displayMonth,
                                )
                                  ? "#F3E5F5"
                                  : "#fff",
                                border:
                                  selectedCell?.fieldName ===
                                    FIELD_NAMES.NRPU &&
                                  selectedCell?.month === row.displayMonth
                                    ? "2px solid #9C27B0 !important"
                                    : "none",
                              }}
                            >
                              {getMonthIndicator(row.month) === "A" &&
                              !showOnlyForecasts ? (
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  padding={1}
                                  alignItems="center"
                                  justifyContent="center"
                                  sx={{ width: "100%" }}
                                  style={{
                                    width: "100%",
                                    position: "sticky",
                                    left: computedLeftOffsets[1],
                                    zIndex: 3,
                                  }}
                                >
                                  <Typography variant="body2">
                                    {formatNumber(
                                      parseFloat(getActual(row.month, "nrpu")),
                                    )}
                                    $
                                  </Typography>
                                  <Typography variant="body2" color="#00000099">
                                    (
                                    {(
                                      getDeviation(row.month, "nrpu") * 100
                                    ).toFixed(1)}
                                    %)
                                  </Typography>
                                </Stack>
                              ) : (
                                <>
                                  <div
                                    onClick={() => {
                                      if (!isMonthEditable(row.displayMonth))
                                        return;
                                      setEditableNRPU(row.displayMonth);
                                      handleCellClick(
                                        FIELD_NAMES.NRPU,
                                        row.displayMonth,
                                      );
                                    }}
                                    style={{
                                      width: "100%",
                                      position: "sticky",
                                      left: computedLeftOffsets[1],
                                      zIndex: 3,
                                    }}
                                  >
                                    <NumericFormat
                                      customInput={TextField}
                                      fullWidth
                                      focused={
                                        editableNRPU === row.displayMonth &&
                                        isMonthEditable(row.displayMonth)
                                      }
                                      color="secondary"
                                      {...(editableNRPU === row.displayMonth
                                        ? {
                                            defaultValue:
                                              monthlyData[row.displayMonth]
                                                ?.nrpu || 0,
                                          }
                                        : {
                                            value:
                                              monthlyData[row.displayMonth]
                                                ?.nrpu || 0,
                                          })}
                                      onValueChange={(values) => {
                                        onNrpuChange(
                                          {
                                            target: {
                                              value: values.floatValue ?? 0,
                                            },
                                          },
                                          row.displayMonth,
                                        );
                                      }}
                                      suffix="$"
                                      onBlur={() => setEditableNRPU(null)}
                                      thousandSeparator=","
                                      inputProps={{
                                        readOnly:
                                          editableNRPU !== row.displayMonth ||
                                          !isMonthEditable(row.displayMonth),
                                        style: { textAlign: "center" },
                                      }}
                                      decimalScale={2}
                                      sx={{
                                        "& .MuiOutlinedInput-root": {
                                          borderRadius: "0px !important",
                                          transition: "transform 0.2s",
                                          ...(editableNRPU ===
                                            row.displayMonth &&
                                            isMonthEditable(
                                              row.displayMonth,
                                            ) && {
                                              boxShadow:
                                                "0 2px 4px -1px rgba(0,0,0,0.1)",
                                              zIndex: 1,
                                            }),
                                          "& fieldset": {
                                            border:
                                              editableNRPU ===
                                                row.displayMonth &&
                                              isMonthEditable(row.displayMonth)
                                                ? "1px solid #9C27B0"
                                                : "none",
                                          },
                                          "&:hover fieldset": {
                                            borderColor:
                                              editableNRPU ===
                                                row.displayMonth &&
                                              isMonthEditable(row.displayMonth)
                                                ? "#9C27B0"
                                                : "none",
                                          },
                                          "&.Mui-focused fieldset": {
                                            borderColor:
                                              editableNRPU ===
                                                row.displayMonth &&
                                              isMonthEditable(row.displayMonth)
                                                ? "#9C27B0"
                                                : "none",
                                          },
                                          "& input": {
                                            padding: "8px !important",
                                            color: "#000000DE",
                                            fontSize: "0.875rem",
                                            backgroundColor: isHighlighted(
                                              FIELD_NAMES.NRPU,
                                              row.displayMonth,
                                            )
                                              ? "#F3E5F5"
                                              : "#FFFFFF",
                                            cursor: isMonthEditable(
                                              row.displayMonth,
                                            )
                                              ? editableNRPU ===
                                                row.displayMonth
                                                ? "text"
                                                : "pointer"
                                              : "pointer",
                                            transition: "background-color 0.2s",
                                          },
                                        },
                                      }}
                                    />
                                  </div>
                                </>
                              )}
                            </TableCell>
                            <TableCell
                              align="left"
                              onClick={() =>
                                handleCellClick(
                                  FIELD_NAMES.FTD,
                                  row.displayMonth,
                                )
                              }
                              sx={{
                                padding: "0px !important",
                                position: "sticky",
                                left: computedLeftOffsets[2],
                                zIndex: 3,
                                backgroundColor: isHighlighted(
                                  FIELD_NAMES.FTD,
                                  row.displayMonth,
                                )
                                  ? "#F3E5F5"
                                  : "#FFFFFF",
                                border:
                                  selectedCell?.fieldName === FIELD_NAMES.FTD &&
                                  selectedCell?.month === row.displayMonth
                                    ? "2px solid #9C27B0 !important"
                                    : "none",
                              }}
                            >
                              {getMonthIndicator(row.month) === "A" &&
                              !showOnlyForecasts ? (
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  padding={1}
                                  alignItems="center"
                                >
                                  <Typography variant="body2">
                                    {formatNumber(
                                      getActual(row.month, "ftd") || 0,
                                    )}
                                  </Typography>
                                  <Typography variant="body2" color="#00000099">
                                    (
                                    {(
                                      getDeviation(row.month, "ftd") * 100
                                    ).toFixed(1)}
                                    %)
                                  </Typography>
                                </Stack>
                              ) : (
                                <div
                                  onClick={() => {
                                    if (!isMonthEditable(row.displayMonth))
                                      return;
                                    setEditableFtd(row.displayMonth);
                                    handleCellClick(
                                      FIELD_NAMES.FTD,
                                      row.displayMonth,
                                    );
                                  }}
                                  style={{
                                    width: "100%",
                                    position: "sticky",
                                    left: computedLeftOffsets[2],
                                    zIndex: 3,
                                  }}
                                >
                                  <NumericFormat
                                    customInput={TextField}
                                    fullWidth
                                    color="secondary"
                                    {...(editableFtd === row.displayMonth
                                      ? {
                                          defaultValue:
                                            monthlyData[row.displayMonth]?.ftd,
                                        }
                                      : {
                                          value:
                                            monthlyData[row.displayMonth]?.ftd,
                                        })}
                                    thousandSeparator=","
                                    suffix=""
                                    decimalScale={editableFtd ? 2 : 0}
                                    onValueChange={(values) => {
                                      onFtdChange(
                                        {
                                          target: { value: values.floatValue },
                                        },
                                        row.displayMonth,
                                      );
                                    }}
                                    focused={
                                      editableFtd === row.displayMonth &&
                                      isMonthEditable(row.displayMonth)
                                    }
                                    onBlur={() => setEditableFtd(null)}
                                    inputProps={{
                                      readOnly:
                                        editableFtd !== row.displayMonth ||
                                        !isMonthEditable(row.displayMonth),
                                    }}
                                    sx={{
                                      "& .MuiOutlinedInput-root": {
                                        borderRadius: "0px !important",
                                        transition:
                                          "transform 0.2s, box-shadow 0.2s",
                                        ...(editableFtd === row.displayMonth &&
                                          isMonthEditable(row.displayMonth) && {
                                            boxShadow:
                                              "0 4px 8px rgba(0,0,0,0.1)",
                                            zIndex: 1,
                                          }),
                                        "& fieldset": {
                                          border:
                                            editableFtd === row.displayMonth &&
                                            isMonthEditable(row.displayMonth)
                                              ? "unset"
                                              : "none",
                                        },
                                        "&:hover fieldset": {
                                          borderColor:
                                            editableFtd === row.displayMonth &&
                                            isMonthEditable(row.displayMonth)
                                              ? "unset"
                                              : "none",
                                        },
                                        "&.Mui-focused fieldset": {
                                          borderColor:
                                            editableFtd === row.displayMonth &&
                                            isMonthEditable(row.displayMonth)
                                              ? "unset"
                                              : "none",
                                        },
                                        "& input": {
                                          padding: "8px !important",
                                          color: "#000000DE",
                                          fontSize: "0.875rem",
                                          backgroundColor: isHighlighted(
                                            FIELD_NAMES.FTD,
                                            row.displayMonth,
                                          )
                                            ? "#F3E5F5"
                                            : "#FFFFFF",
                                          cursor: isMonthEditable(
                                            row.displayMonth,
                                          )
                                            ? editableFtd === row.displayMonth
                                              ? "text"
                                              : "pointer"
                                            : "pointer",
                                          transition: "background-color 0.2s",
                                          textAlign: "center",
                                        },
                                      },
                                    }}
                                  />
                                </div>
                              )}
                            </TableCell>
                            <TableCell
                              align="center"
                              onClick={() =>
                                handleCellClick(
                                  FIELD_NAMES.AVG_NET_REV,
                                  row.displayMonth,
                                )
                              }
                              sx={{
                                padding: "0px !important",
                                position: "sticky",
                                left: computedLeftOffsets[3],
                                zIndex: 3,
                                width: "150px",
                                minWidth: "150px",
                                maxWidth: "150px",
                                whiteSpace: "nowrap",
                                backgroundColor: isHighlighted(
                                  FIELD_NAMES.AVG_NET_REV,
                                  row.displayMonth,
                                )
                                  ? "#F3E5F5"
                                  : "#FFFFFF",
                                border:
                                  selectedCell?.fieldName ===
                                    FIELD_NAMES.AVG_NET_REV &&
                                  selectedCell?.month === row.displayMonth
                                    ? "2px solid #9C27B0 !important"
                                    : "none",
                              }}
                            >
                              {getMonthIndicator(row.month) === "A" &&
                              !showOnlyForecasts ? (
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  padding={1}
                                  alignItems="center"
                                  justifyContent="center"
                                  sx={{ width: "100%" }}
                                >
                                  <Typography variant="body2">
                                    {`${Number(
                                      getActual(
                                        row.month,
                                        "avg_net_rev_per_active_user",
                                      ),
                                    ).toFixed(2)}$`}
                                  </Typography>
                                  <Typography variant="body2" color="#00000099">
                                    (
                                    {(
                                      getDeviation(
                                        row.month,
                                        "avg_net_rev_per_active_user",
                                      ) * 100
                                    ).toFixed(1)}{" "}
                                    %)
                                  </Typography>
                                </Stack>
                              ) : (
                                <div
                                  onClick={() => {
                                    if (!isMonthEditable(row.displayMonth))
                                      return;
                                    setEditableAvgNetREv(row.displayMonth);
                                    handleCellClick(
                                      FIELD_NAMES.AVG_NET_REV,
                                      row.displayMonth,
                                    );
                                  }}
                                  style={{
                                    width: "100%",
                                    position: "sticky",
                                    left: computedLeftOffsets[3],
                                    zIndex: 4,
                                  }}
                                >
                                  <NumericFormat
                                    customInput={TextField}
                                    fullWidth
                                    focused={
                                      editableAvgNetREv === row.displayMonth &&
                                      isMonthEditable(row.displayMonth)
                                    }
                                    color="secondary"
                                    {...(editableAvgNetREv === row.displayMonth
                                      ? {
                                          defaultValue: `${Number(monthlyData[row.displayMonth]?.avgNetRevPerActiveUser).toFixed(2)}$`,
                                        }
                                      : {
                                          value: `${Number(monthlyData[row.displayMonth]?.avgNetRevPerActiveUser).toFixed(2)}$`,
                                        })}
                                    decimalScale={2}
                                    onValueChange={(values) => {
                                      onAvgNetRevPerActiveUserChange(
                                        {
                                          target: { value: values.floatValue },
                                        },
                                        row.displayMonth,
                                      );
                                    }}
                                    thousandSeparator=","
                                    onBlur={() => setEditableAvgNetREv(null)}
                                    inputProps={{
                                      readOnly:
                                        editableAvgNetREv !==
                                          row.displayMonth ||
                                        !isMonthEditable(row.displayMonth),
                                      style: { textAlign: "center" },
                                    }}
                                    suffix="$"
                                    sx={{
                                      "& .MuiOutlinedInput-root": {
                                        borderRadius: "0px !important",
                                        transition:
                                          "transform 0.2s, box-shadow 0.2s",
                                        ...(editableAvgNetREv ===
                                          row.displayMonth &&
                                          isMonthEditable(row.displayMonth) && {
                                            boxShadow:
                                              "0 4px 8px rgba(0,0,0,0.1)",
                                            zIndex: 1,
                                          }),
                                        "& fieldset": {
                                          border:
                                            editableAvgNetREv ===
                                              row.displayMonth &&
                                            isMonthEditable(row.displayMonth)
                                              ? "unset"
                                              : "none",
                                        },
                                        "&:hover fieldset": {
                                          borderColor:
                                            editableAvgNetREv ===
                                              row.displayMonth &&
                                            isMonthEditable(row.displayMonth)
                                              ? "unset"
                                              : "none",
                                        },
                                        "&.Mui-focused fieldset": {
                                          borderColor:
                                            editableAvgNetREv ===
                                              row.displayMonth &&
                                            isMonthEditable(row.displayMonth)
                                              ? "unset"
                                              : "none",
                                        },
                                        "& input": {
                                          padding: "8px !important",
                                          color: "#000000DE",
                                          fontSize: "0.875rem",
                                          backgroundColor: isHighlighted(
                                            FIELD_NAMES.AVG_NET_REV,
                                            row.displayMonth,
                                          )
                                            ? "#F3E5F5"
                                            : "#FFFFFF",
                                          cursor: isMonthEditable(
                                            row.displayMonth,
                                          )
                                            ? editableAvgNetREv ===
                                              row.displayMonth
                                              ? "text"
                                              : "pointer"
                                            : "pointer",
                                          transition: "background-color 0.2s",
                                          textAlign: "center",
                                        },
                                      },
                                    }}
                                  />
                                </div>
                              )}
                            </TableCell>
                            <TableCell
                              align="left"
                              onClick={() =>
                                handleCellClick(
                                  FIELD_NAMES.CPA_PV,
                                  row.displayMonth,
                                )
                              }
                              sx={{
                                padding: "0px !important",
                                position: "sticky",
                                left: computedLeftOffsets[4],
                                zIndex: 5,
                                backgroundColor: isHighlighted(
                                  FIELD_NAMES.CPA_PV,
                                  row.displayMonth,
                                )
                                  ? "#F3E5F5"
                                  : "#FFFFFF",
                                border:
                                  selectedCell?.fieldName ===
                                    FIELD_NAMES.CPA_PV &&
                                  selectedCell?.month === row.displayMonth
                                    ? "2px solid #9C27B0 !important"
                                    : "none",
                              }}
                            >
                              {getMonthIndicator(row.month) === "A" &&
                              !showOnlyForecasts ? (
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  padding={1}
                                  alignItems="center"
                                  justifyContent="center"
                                  sx={{ width: "100%" }}
                                >
                                  <Typography variant="body2">
                                    {formatNumber(
                                      getActual(row.month, "cpa_pv"),
                                    ) || 0}
                                    $
                                  </Typography>
                                  <Typography variant="body2" color="#00000099">
                                    (
                                    {(
                                      getDeviation(row.month, "cpa_pv") * 100
                                    ).toFixed(1)}
                                    %)
                                  </Typography>
                                </Stack>
                              ) : (
                                <div
                                  onClick={() => {
                                    if (!isMonthEditable(row.displayMonth))
                                      return;
                                    setEditableCPAPV(row.displayMonth);
                                    handleCellClick(
                                      FIELD_NAMES.CPA_PV,
                                      row.displayMonth,
                                    );
                                  }}
                                  style={{
                                    width: "100%",
                                    position: "sticky",
                                    left: computedLeftOffsets[4],
                                    zIndex: 5,
                                  }}
                                >
                                  <NumericFormat
                                    customInput={TextField}
                                    fullWidth
                                    focused={
                                      editableCPAPV === row.displayMonth &&
                                      isMonthEditable(row.displayMonth)
                                    }
                                    color="secondary"
                                    {...(editableCPAPV === row.displayMonth
                                      ? {
                                          defaultValue:
                                            monthlyData[row.displayMonth]
                                              ?.cpaPv,
                                        }
                                      : {
                                          value:
                                            monthlyData[row.displayMonth]
                                              ?.cpaPv,
                                        })}
                                    decimalScale={editableCPAPV ? 2 : 0}
                                    onValueChange={(values) => {
                                      onCpaPvChange(
                                        {
                                          target: { value: values.floatValue },
                                        },
                                        row.displayMonth,
                                      );
                                    }}
                                    onBlur={() => setEditableCPAPV(null)}
                                    thousandSeparator=","
                                    suffix="$"
                                    inputProps={{
                                      readOnly:
                                        editableCPAPV !== row.displayMonth ||
                                        !isMonthEditable(row.displayMonth),
                                      style: { textAlign: "center" },
                                    }}
                                    sx={{
                                      "& .MuiOutlinedInput-root": {
                                        borderRadius: "0px !important",
                                        transition:
                                          "transform 0.2s, box-shadow 0.2s",
                                        ...(editableCPAPV ===
                                          row.displayMonth &&
                                          isMonthEditable(row.displayMonth) && {
                                            boxShadow:
                                              "0 4px 8px rgba(0,0,0,0.1)",
                                            zIndex: 1,
                                          }),
                                        "& fieldset": {
                                          border:
                                            editableCPAPV ===
                                              row.displayMonth &&
                                            isMonthEditable(row.displayMonth)
                                              ? "unset"
                                              : "none",
                                        },
                                        "&:hover fieldset": {
                                          borderColor:
                                            editableCPAPV ===
                                              row.displayMonth &&
                                            isMonthEditable(row.displayMonth)
                                              ? "unset"
                                              : "none",
                                        },
                                        "&.Mui-focused fieldset": {
                                          borderColor:
                                            editableCPAPV ===
                                              row.displayMonth &&
                                            isMonthEditable(row.displayMonth)
                                              ? "unset"
                                              : "none",
                                        },
                                        "& input": {
                                          padding: "8px !important",
                                          color: "#000000DE",
                                          fontSize: "0.875rem",
                                          backgroundColor: isHighlighted(
                                            FIELD_NAMES.CPA_PV,
                                            row.displayMonth,
                                          )
                                            ? "#F3E5F5"
                                            : "#FFFFFF",
                                          cursor: isMonthEditable(
                                            row.displayMonth,
                                          )
                                            ? editableCPAPV === row.displayMonth
                                              ? "text"
                                              : "pointer"
                                            : "pointer",
                                          transition: "background-color 0.2s",
                                        },
                                      },
                                    }}
                                  />
                                </div>
                              )}
                            </TableCell>
                            <TableCell
                              align="center"
                              onClick={() =>
                                handleCellClick(
                                  FIELD_NAMES.CVR,
                                  row.displayMonth,
                                )
                              }
                              sx={{
                                padding: "0px !important",
                                position: "sticky",
                                left: computedLeftOffsets[5],
                                zIndex: 3,
                                backgroundColor: isHighlighted(
                                  FIELD_NAMES.CVR,
                                  row.displayMonth,
                                )
                                  ? "#F3E5F5"
                                  : "#FFFFFF",
                                border:
                                  selectedCell?.fieldName === FIELD_NAMES.CVR &&
                                  selectedCell?.month === row.displayMonth
                                    ? "2px solid #9C27B0 !important"
                                    : "none",
                              }}
                            >
                              {getMonthIndicator(row.month) === "A" &&
                              !showOnlyForecasts ? (
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  padding={1}
                                  alignItems="center"
                                  justifyContent="center"
                                  sx={{ width: "100%" }}
                                >
                                  <Typography variant="body2">
                                    {formatPercentageNonRound(
                                      getActual(row.month, "CVR"),
                                    )}
                                  </Typography>
                                  <Typography variant="body2" color="#00000099">
                                    (
                                    {(
                                      getDeviation(row.month, "CVR") * 100
                                    ).toFixed(1)}
                                    %)
                                  </Typography>
                                </Stack>
                              ) : (
                                <div
                                  onClick={() => {
                                    if (!isMonthEditable(row.displayMonth))
                                      return;
                                    setEditableCVR(row.displayMonth);
                                    handleCellClick(
                                      FIELD_NAMES.CVR,
                                      row.displayMonth,
                                    );
                                  }}
                                  style={{
                                    width: "100%",
                                    position: "sticky",
                                    left: computedLeftOffsets[5],
                                    zIndex: 3,
                                  }}
                                >
                                  <NumericFormat
                                    customInput={TextField}
                                    fullWidth
                                    focused={
                                      editableCVR === row.displayMonth &&
                                      isMonthEditable(row.displayMonth)
                                    }
                                    color="secondary"
                                    {...(editableCVR === row.displayMonth
                                      ? {
                                          defaultValue: `${Number(monthlyData[row.displayMonth]?.cvr * 100).toFixed(2)}`,
                                        }
                                      : {
                                          value: `${Number(monthlyData[row.displayMonth]?.cvr * 100).toFixed(2)}`,
                                        })}
                                    onValueChange={(values) => {
                                      onCvrChange(
                                        {
                                          target: {
                                            value:
                                              (values.floatValue ?? 0) / 100,
                                          },
                                        },
                                        row.displayMonth,
                                      );
                                    }}
                                    thousandSeparator=","
                                    onBlur={() => setEditableCVR(null)}
                                    inputProps={{
                                      readOnly:
                                        editableCVR !== row.displayMonth ||
                                        !isMonthEditable(row.displayMonth),
                                      style: { textAlign: "center" },
                                    }}
                                    decimalScale={2}
                                    suffix="%"
                                    sx={{
                                      "& .MuiOutlinedInput-root": {
                                        borderRadius: "0px !important",
                                        transition:
                                          "transform 0.2s, box-shadow 0.2s",
                                        ...(editableCVR === row.displayMonth &&
                                          isMonthEditable(row.displayMonth) && {
                                            boxShadow:
                                              "0 4px 8px rgba(0,0,0,0.1)",
                                            zIndex: 1,
                                          }),
                                        "& fieldset": {
                                          border:
                                            editableCVR === row.displayMonth &&
                                            isMonthEditable(row.displayMonth)
                                              ? "unset"
                                              : "none",
                                        },
                                        "&:hover fieldset": {
                                          borderColor:
                                            editableCVR === row.displayMonth &&
                                            isMonthEditable(row.displayMonth)
                                              ? "unset"
                                              : "none",
                                        },
                                        "&.Mui-focused fieldset": {
                                          borderColor:
                                            editableCVR === row.displayMonth &&
                                            isMonthEditable(row.displayMonth)
                                              ? "unset"
                                              : "none",
                                        },
                                        "& input": {
                                          padding: "8px !important",
                                          color: "#000000DE",
                                          fontSize: "0.875rem",
                                          backgroundColor: isHighlighted(
                                            FIELD_NAMES.CVR,
                                            row.displayMonth,
                                          )
                                            ? "#F3E5F5"
                                            : "#FFFFFF",
                                          cursor: isMonthEditable(
                                            row.displayMonth,
                                          )
                                            ? editableCVR === row.displayMonth
                                              ? "text"
                                              : "pointer"
                                            : "pointer",
                                          transition: "background-color 0.2s",
                                        },
                                      },
                                    }}
                                  />
                                </div>
                              )}
                            </TableCell>
                            <TableCell
                              align="left"
                              onClick={() =>
                                handleCellClick(
                                  FIELD_NAMES.PROFIT,
                                  row.displayMonth,
                                )
                              }
                              sx={{
                                padding: "0px !important",
                                position: "sticky",
                                left: computedLeftOffsets[6],
                                zIndex: 3,
                                borderRight: "1px solid #e0e0e0 !important",
                                backgroundColor: isHighlighted(
                                  FIELD_NAMES.PROFIT,
                                  row.displayMonth,
                                )
                                  ? "#F3E5F5"
                                  : "#FFFFFF",
                                border:
                                  selectedCell?.fieldName ===
                                    FIELD_NAMES.PROFIT &&
                                  selectedCell?.month === row.displayMonth
                                    ? "2px solid #9C27B0 !important"
                                    : "none",
                              }}
                            >
                              {getMonthIndicator(row.month) === "A" &&
                              !showOnlyForecasts ? (
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  padding={1}
                                  alignItems="center"
                                  justifyContent="center"
                                  sx={{ width: "100%" }}
                                >
                                  <Typography variant="body2">
                                    {formatNumber(
                                      getActual(row.month, "profit"),
                                    ) || 0}
                                    $
                                  </Typography>
                                  <Typography variant="body2" color="#00000099">
                                    (
                                    {(
                                      getDeviation(row.month, "profit") * 100
                                    ).toFixed(1)}
                                    %)
                                  </Typography>
                                </Stack>
                              ) : (
                                <div
                                  onClick={() => {
                                    if (!isMonthEditable(row.displayMonth))
                                      return;
                                    setEditableProfit(row.displayMonth);
                                    handleCellClick(
                                      FIELD_NAMES.PROFIT,
                                      row.displayMonth,
                                    );
                                  }}
                                  style={{
                                    width: "100%",
                                    position: "sticky",
                                    left: computedLeftOffsets[6],
                                    zIndex: 3,
                                  }}
                                >
                                  <NumericFormat
                                    customInput={TextField}
                                    fullWidth
                                    focused={
                                      editableProfit === row.displayMonth &&
                                      isMonthEditable(row.displayMonth)
                                    }
                                    color="secondary"
                                    {...(editableProfit === row.displayMonth
                                      ? {
                                          defaultValue:
                                            monthlyData[row.displayMonth]
                                              ?.profit,
                                        }
                                      : {
                                          value:
                                            monthlyData[row.displayMonth]
                                              ?.profit,
                                        })}
                                    decimalScale={editableProfit ? 2 : 0}
                                    onValueChange={(values) => {
                                      onProfitChange(
                                        {
                                          target: { value: values.floatValue },
                                        },
                                        row.displayMonth,
                                      );
                                    }}
                                    thousandSeparator=","
                                    onBlur={() => setEditableProfit(null)}
                                    inputProps={{
                                      readOnly:
                                        editableProfit !== row.displayMonth ||
                                        !isMonthEditable(row.displayMonth),
                                      style: { textAlign: "center" },
                                    }}
                                    suffix="$"
                                    sx={{
                                      "& .MuiOutlinedInput-root": {
                                        borderRadius: "0px !important",
                                        transition:
                                          "transform 0.2s, box-shadow 0.2s",
                                        ...(editableProfit ===
                                          row.displayMonth &&
                                          isMonthEditable(row.displayMonth) && {
                                            boxShadow:
                                              "0 4px 8px rgba(0,0,0,0.1)",
                                            zIndex: 1,
                                          }),
                                        "& fieldset": {
                                          border:
                                            editableProfit ===
                                              row.displayMonth &&
                                            isMonthEditable(row.displayMonth)
                                              ? "unset"
                                              : "none",
                                        },
                                        "&:hover fieldset": {
                                          borderColor:
                                            editableProfit ===
                                              row.displayMonth &&
                                            isMonthEditable(row.displayMonth)
                                              ? "unset"
                                              : "none",
                                        },
                                        "&.Mui-focused fieldset": {
                                          borderColor:
                                            editableProfit ===
                                              row.displayMonth &&
                                            isMonthEditable(row.displayMonth)
                                              ? "unset"
                                              : "none",
                                        },
                                        "& input": {
                                          padding: "8px !important",
                                          color: "#000000DE",
                                          fontSize: "0.875rem",
                                          backgroundColor: isHighlighted(
                                            FIELD_NAMES.PROFIT,
                                            row.displayMonth,
                                          )
                                            ? "#F3E5F5"
                                            : "#FFFFFF",
                                          cursor: isMonthEditable(
                                            row.displayMonth,
                                          )
                                            ? editableProfit ===
                                              row.displayMonth
                                              ? "text"
                                              : "pointer"
                                            : "pointer",
                                          transition: "background-color 0.2s",
                                        },
                                      },
                                    }}
                                  />
                                </div>
                              )}
                            </TableCell>
                            <TableCell
                              align="center"
                              onClick={() =>
                                handleCellClick(
                                  FIELD_NAMES.ACTIVE_USERS,
                                  row.displayMonth,
                                )
                              }
                              sx={{
                                padding: "0px 8px",
                                backgroundColor: isHighlighted(
                                  FIELD_NAMES.ACTIVE_USERS,
                                  row.displayMonth,
                                )
                                  ? "#F3E5F5"
                                  : "#F5F5F5",
                                border:
                                  selectedCell?.fieldName ===
                                    FIELD_NAMES.ACTIVE_USERS &&
                                  selectedCell?.month === row.displayMonth
                                    ? "2px solid #9C27B0 !important"
                                    : "none",
                              }}
                            >
                              {getMonthIndicator(row.month) === "A" &&
                              !showOnlyForecasts ? (
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  padding={1}
                                  alignItems="center"
                                >
                                  <Typography variant="body2">
                                    {formatNumber(
                                      getActual(row.month, "active_users"),
                                    ) || 0}
                                  </Typography>
                                  <Typography variant="body2" color="#00000099">
                                    (
                                    {(
                                      getDeviation(row.month, "active_users") *
                                      100
                                    ).toFixed(1)}
                                    %)
                                  </Typography>
                                </Stack>
                              ) : (
                                <>
                                  {formatNumber(
                                    monthlyData[row.displayMonth]
                                      ?.activeUsers ??
                                      row.activeUsers ??
                                      0,
                                  )}
                                </>
                              )}
                            </TableCell>
                            <TableCell
                              align="center"
                              onClick={() =>
                                handleCellClick(
                                  FIELD_NAMES.OLD_USERS,
                                  row.displayMonth,
                                )
                              }
                              sx={{
                                padding: "0px 8px",
                                backgroundColor: isHighlighted(
                                  FIELD_NAMES.OLD_USERS,
                                  row.displayMonth,
                                )
                                  ? "#F3E5F5"
                                  : "#F5F5F5",
                                border:
                                  selectedCell?.fieldName ===
                                    FIELD_NAMES.OLD_USERS &&
                                  selectedCell?.month === row.displayMonth
                                    ? "2px solid #9C27B0 !important"
                                    : "none",
                              }}
                            >
                              {getMonthIndicator(row.month) === "A" &&
                              !showOnlyForecasts ? (
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  padding={1}
                                  alignItems="center"
                                >
                                  <Typography variant="body2">
                                    {formatNumber(
                                      getActual(row.month, "old_active_users"),
                                    ) || 0}
                                  </Typography>
                                  <Typography variant="body2" color="#00000099">
                                    (
                                    {(
                                      getDeviation(
                                        row.month,
                                        "old_active_users",
                                      ) * 100
                                    ).toFixed(1)}
                                    %)
                                  </Typography>
                                </Stack>
                              ) : (
                                <>
                                  {formatNumber(
                                    monthlyData[row.displayMonth]
                                      ?.oldActiveUsers ??
                                      row.oldActiveUsers ??
                                      0,
                                  )}
                                </>
                              )}
                            </TableCell>
                            <TableCell
                              align="center"
                              onClick={() =>
                                handleCellClick(
                                  FIELD_NAMES.ACQUIRED_USERS,
                                  row.displayMonth,
                                )
                              }
                              sx={{
                                padding: "0px 8px",
                                backgroundColor: isHighlighted(
                                  FIELD_NAMES.ACQUIRED_USERS,
                                  row.displayMonth,
                                )
                                  ? "#F3E5F5"
                                  : "#F5F5F5",
                                border:
                                  selectedCell?.fieldName ===
                                    FIELD_NAMES.ACQUIRED_USERS &&
                                  selectedCell?.month === row.displayMonth
                                    ? "2px solid #9C27B0 !important"
                                    : "none",
                              }}
                            >
                              {getMonthIndicator(row.month) === "A" &&
                              !showOnlyForecasts ? (
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  padding={1}
                                  alignItems="center"
                                >
                                  <Typography variant="body2">
                                    {formatNumber(
                                      getActual(
                                        row.month,
                                        "acquired_active_users",
                                      ) || 0,
                                    )}
                                  </Typography>
                                  <Typography variant="body2" color="#00000099">
                                    (
                                    {(
                                      getDeviation(
                                        row.month,
                                        "acquired_active_users",
                                      ) * 100
                                    ).toFixed(1)}
                                    %)
                                  </Typography>
                                </Stack>
                              ) : (
                                <>
                                  {formatNumber(
                                    monthlyData[row.displayMonth]
                                      ?.acquiredUsers ??
                                      row.acquiredUsers ??
                                      0,
                                  )}
                                </>
                              )}
                            </TableCell>
                            <TableCell
                              align="center"
                              onClick={() =>
                                handleCellClick(
                                  FIELD_NAMES.GROWTH,
                                  row.displayMonth,
                                )
                              }
                              sx={{
                                padding: "0px !important",
                                backgroundColor: isHighlighted(
                                  FIELD_NAMES.GROWTH,
                                  row.displayMonth,
                                )
                                  ? "#F3E5F5"
                                  : "#F5F5F5",
                                border:
                                  selectedCell?.fieldName ===
                                    FIELD_NAMES.GROWTH &&
                                  selectedCell?.month === row.displayMonth
                                    ? "2px solid #9C27B0 !important"
                                    : "none",
                              }}
                            >
                              {getMonthIndicator(row.month) === "A" &&
                              !showOnlyForecasts ? (
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  padding={1}
                                  alignItems="center"
                                  justifyContent="center"
                                  sx={{ width: "100%" }}
                                >
                                  <Typography variant="body2">
                                    {formatPercentageNonRoundUptoTwo(
                                      getActual(row.month, "growth"),
                                    )}
                                  </Typography>
                                  <Typography variant="body2" color="#00000099">
                                    (
                                    {(
                                      getDeviation(row.month, "growth") * 100
                                    ).toFixed(2)}
                                    %)
                                  </Typography>
                                </Stack>
                              ) : (
                                <>
                                  {formatPercentageNonRoundUptoTwo(
                                    monthlyData[row.displayMonth]?.growth ||
                                      row.growth,
                                  )}
                                </>
                              )}
                            </TableCell>
                            <TableCell
                              align="center"
                              onClick={() =>
                                handleCellClick(
                                  FIELD_NAMES.NET_REVENUE,
                                  row.displayMonth,
                                )
                              }
                              sx={{
                                padding: "0px !important",
                                backgroundColor: isHighlighted(
                                  FIELD_NAMES.NET_REVENUE,
                                  row.displayMonth,
                                )
                                  ? "#F3E5F5"
                                  : "#F5F5F5",
                                border:
                                  selectedCell?.fieldName ===
                                    FIELD_NAMES.NET_REVENUE &&
                                  selectedCell?.month === row.displayMonth
                                    ? "2px solid #9C27B0 !important"
                                    : "none",
                              }}
                            >
                              {getMonthIndicator(row.month) === "A" &&
                              !showOnlyForecasts ? (
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  padding={1}
                                  alignItems="center"
                                >
                                  <Typography variant="body2">
                                    {formatNumber(
                                      getActual(row.month, "net_rev"),
                                    ) || 0}
                                    $
                                  </Typography>
                                  <Typography variant="body2" color="#00000099">
                                    (
                                    {(
                                      getDeviation(row.month, "net_rev") * 100
                                    ).toFixed(1)}
                                    %)
                                  </Typography>
                                </Stack>
                              ) : (
                                <>
                                  {formatNumber(
                                    monthlyData[row.displayMonth]?.estNetRev ??
                                      row.estNetRev ??
                                      0,
                                  )}
                                  $
                                </>
                              )}
                            </TableCell>
                            <TableCell
                              align="left"
                              onClick={() =>
                                handleCellClick(
                                  FIELD_NAMES.ESTIMATED_REVENUE_SHARE,
                                  row.displayMonth,
                                )
                              }
                              sx={{
                                padding: "0px 8px",
                                backgroundColor: isHighlighted(
                                  FIELD_NAMES.ESTIMATED_REVENUE_SHARE,
                                  row.displayMonth,
                                )
                                  ? "#F3E5F5"
                                  : "#F5F5F5",
                                border:
                                  selectedCell?.fieldName ===
                                    FIELD_NAMES.ESTIMATED_REVENUE_SHARE &&
                                  selectedCell?.month === row.displayMonth
                                    ? "2px solid #9C27B0 !important"
                                    : "none",
                              }}
                            >
                              {getMonthIndicator(row.month) === "A" &&
                              !showOnlyForecasts ? (
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  padding={1}
                                  alignItems="center"
                                >
                                  <Typography variant="body2">
                                    {formatNumber(
                                      getActual(
                                        row.month,
                                        "rs_income_dollar",
                                      ) || 0,
                                    )}
                                    $
                                  </Typography>
                                  <Typography variant="body2" color="#00000099">
                                    (
                                    {(
                                      getDeviation(
                                        row.month,
                                        "rs_income_dollar",
                                      ) * 100
                                    ).toFixed(1)}
                                    %)
                                  </Typography>
                                </Stack>
                              ) : (
                                <>
                                  {formatNumber(
                                    monthlyData[row.displayMonth]?.estRs ??
                                      row.estRs ??
                                      0,
                                  )}
                                  $
                                </>
                              )}
                            </TableCell>
                            <TableCell
                              align="center"
                              onClick={() =>
                                handleCellClick(
                                  FIELD_NAMES.RS_PV,
                                  row.displayMonth,
                                )
                              }
                              sx={{
                                padding: "0px 8px",
                                backgroundColor: isHighlighted(
                                  FIELD_NAMES.RS_PV,
                                  row.displayMonth,
                                )
                                  ? "#F3E5F5"
                                  : "#F5F5F5",
                                border:
                                  selectedCell?.fieldName ===
                                    FIELD_NAMES.RS_PV &&
                                  selectedCell?.month === row.displayMonth
                                    ? "2px solid #9C27B0 !important"
                                    : "none",
                              }}
                            >
                              {getMonthIndicator(row.month) === "A" &&
                              !showOnlyForecasts ? (
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  padding={1}
                                  alignItems="center"
                                >
                                  <Typography variant="body2">
                                    {formatNumber(
                                      getActual(row.month, "rs_pv"),
                                    ) || 0}
                                    $
                                  </Typography>
                                  <Typography variant="body2" color="#00000099">
                                    (
                                    {(
                                      getDeviation(row.month, "rs_pv") * 100
                                    ).toFixed(1)}
                                    %)
                                  </Typography>
                                </Stack>
                              ) : (
                                <>
                                  {formatNumber(
                                    monthlyData[row.displayMonth]?.rsPv ??
                                      row.rsPV ??
                                      0,
                                  )}
                                  $
                                </>
                              )}
                            </TableCell>
                            <TableCell
                              align="center"
                              onClick={() =>
                                handleCellClick(
                                  FIELD_NAMES.CPA_INCOME,
                                  row.displayMonth,
                                )
                              }
                              sx={{
                                padding: "0px 8px",
                                backgroundColor: isHighlighted(
                                  FIELD_NAMES.CPA_INCOME,
                                  row.displayMonth,
                                )
                                  ? "#F3E5F5"
                                  : "#F5F5F5",
                                border:
                                  selectedCell?.fieldName ===
                                    FIELD_NAMES.CPA_INCOME &&
                                  selectedCell?.month === row.displayMonth
                                    ? "2px solid #9C27B0 !important"
                                    : "none",
                              }}
                            >
                              {getMonthIndicator(row.month) === "A" &&
                              !showOnlyForecasts ? (
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  padding={1}
                                  alignItems="center"
                                >
                                  <Typography variant="body2">
                                    {formatNumber(
                                      getActual(row.month, "cpa_income_dollar"),
                                    ) || 0}
                                    $
                                  </Typography>
                                  <Typography variant="body2" color="#00000099">
                                    (
                                    {(
                                      getDeviation(
                                        row.month,
                                        "cpa_income_dollar",
                                      ) * 100
                                    ).toFixed(1)}
                                    %)
                                  </Typography>
                                </Stack>
                              ) : (
                                <>
                                  {formatNumber(
                                    monthlyData[row.displayMonth]
                                      ?.estCpaIncome || row.cpaIncomeDollar,
                                  )}
                                  $
                                </>
                              )}
                            </TableCell>
                            <TableCell
                              align="center"
                              onClick={() =>
                                handleCellClick(
                                  FIELD_NAMES.TOTAL_INCOME,
                                  row.displayMonth,
                                )
                              }
                              sx={{
                                padding: "0px 8px",
                                backgroundColor: isHighlighted(
                                  FIELD_NAMES.TOTAL_INCOME,
                                  row.displayMonth,
                                )
                                  ? "#F3E5F5"
                                  : "#F5F5F5",
                                border:
                                  selectedCell?.fieldName ===
                                    FIELD_NAMES.TOTAL_INCOME &&
                                  selectedCell?.month === row.displayMonth
                                    ? "2px solid #9C27B0 !important"
                                    : "none",
                              }}
                            >
                              {getMonthIndicator(row.month) === "A" &&
                              !showOnlyForecasts ? (
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  padding={1}
                                  alignItems="center"
                                >
                                  <Typography variant="body2">
                                    {formatNumber(
                                      getActual(row.month, "total_income"),
                                    ) || 0}
                                    $
                                  </Typography>
                                  <Typography variant="body2" color="#00000099">
                                    (
                                    {(
                                      getDeviation(row.month, "total_income") *
                                      100
                                    ).toFixed(1)}
                                    %)
                                  </Typography>
                                </Stack>
                              ) : (
                                <>
                                  {formatNumber(
                                    monthlyData[row.displayMonth]
                                      ?.totalIncome || row.totalIncome,
                                  )}
                                  $
                                </>
                              )}
                            </TableCell>
                            <TableCell
                              align="center"
                              onClick={() =>
                                handleCellClick(
                                  FIELD_NAMES.CLICKS,
                                  row.displayMonth,
                                )
                              }
                              sx={{
                                padding: "0px !important",
                                backgroundColor: isHighlighted(
                                  FIELD_NAMES.CLICKS,
                                  row.displayMonth,
                                )
                                  ? "#F3E5F5"
                                  : "#F5F5F5",
                                border:
                                  selectedCell?.fieldName ===
                                    FIELD_NAMES.CLICKS &&
                                  selectedCell?.month === row.displayMonth
                                    ? "2px solid #9C27B0 !important"
                                    : "none",
                              }}
                            >
                              {getMonthIndicator(row.month) === "A" &&
                              !showOnlyForecasts ? (
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  padding={1}
                                  alignItems="center"
                                >
                                  <Typography variant="body2">
                                    {formatNumber(
                                      getActual(row.month, "clicks"),
                                    ) || 0}
                                  </Typography>
                                  <Typography variant="body2" color="#00000099">
                                    (
                                    {(
                                      getDeviation(row.month, "clicks") * 100
                                    ).toFixed(1)}
                                    %)
                                  </Typography>
                                </Stack>
                              ) : (
                                <>
                                  {formatNumber(
                                    monthlyData[row.displayMonth]?.clicks ||
                                      row.clicks,
                                  )}
                                </>
                              )}
                            </TableCell>
                            <TableCell
                              align="center"
                              onClick={() =>
                                handleCellClick(
                                  FIELD_NAMES.EPC_CPA,
                                  row.displayMonth,
                                )
                              }
                              sx={{
                                padding: "0px !important",
                                backgroundColor: isHighlighted(
                                  FIELD_NAMES.EPC_CPA,
                                  row.displayMonth,
                                )
                                  ? "#F3E5F5"
                                  : "#F5F5F5",
                                border:
                                  selectedCell?.fieldName ===
                                    FIELD_NAMES.EPC_CPA &&
                                  selectedCell?.month === row.displayMonth
                                    ? "2px solid #9C27B0 !important"
                                    : "none",
                              }}
                            >
                              {getMonthIndicator(row.month) === "A" &&
                              !showOnlyForecasts ? (
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  padding={1}
                                  alignItems="center"
                                >
                                  <Typography variant="body2">
                                    {formatNumber(
                                      getActual(row.month, "EPC_CPA"),
                                    ) || 0}
                                    $
                                  </Typography>
                                  <Typography variant="body2" color="#00000099">
                                    (
                                    {(
                                      getDeviation(row.month, "EPC_CPA") * 100
                                    ).toFixed(1)}
                                    %)
                                  </Typography>
                                </Stack>
                              ) : (
                                <>
                                  {formatNumber(
                                    monthlyData[row.displayMonth]?.epcCpa ||
                                      row.epcCpa,
                                  )}
                                  $
                                </>
                              )}
                            </TableCell>
                            <TableCell
                              align="center"
                              onClick={() =>
                                handleCellClick(
                                  FIELD_NAMES.EPC_RS,
                                  row.displayMonth,
                                )
                              }
                              sx={{
                                padding: "0px !important",
                                backgroundColor: isHighlighted(
                                  FIELD_NAMES.EPC_RS,
                                  row.displayMonth,
                                )
                                  ? "#F3E5F5"
                                  : "#F5F5F5",
                                border:
                                  selectedCell?.fieldName ===
                                    FIELD_NAMES.EPC_RS &&
                                  selectedCell?.month === row.displayMonth
                                    ? "2px solid #9C27B0 !important"
                                    : "none",
                              }}
                            >
                              {getMonthIndicator(row.month) === "A" &&
                              !showOnlyForecasts ? (
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  padding={1}
                                  alignItems="center"
                                >
                                  <Typography variant="body2">
                                    {formatNumber(
                                      getActual(row.month, "EPC_RS"),
                                    ) || 0}
                                    $
                                  </Typography>
                                  <Typography variant="body2" color="#00000099">
                                    (
                                    {(
                                      getDeviation(row.month, "EPC_RS") * 100
                                    ).toFixed(1)}
                                    %)
                                  </Typography>
                                </Stack>
                              ) : (
                                <>
                                  {formatNumber(
                                    monthlyData[row.displayMonth]?.epcRs ||
                                      row.epcRs,
                                  )}
                                  $
                                </>
                              )}
                            </TableCell>
                            <TableCell
                              align="center"
                              onClick={() =>
                                handleCellClick(
                                  FIELD_NAMES.EPC,
                                  row.displayMonth,
                                )
                              }
                              sx={{
                                padding: "0px !important",
                                backgroundColor: isHighlighted(
                                  FIELD_NAMES.EPC,
                                  row.displayMonth,
                                )
                                  ? "#F3E5F5"
                                  : "#F5F5F5",
                                border:
                                  selectedCell?.fieldName === FIELD_NAMES.EPC &&
                                  selectedCell?.month === row.displayMonth
                                    ? "2px solid #9C27B0 !important"
                                    : "none",
                              }}
                            >
                              {getMonthIndicator(row.month) === "A" &&
                              !showOnlyForecasts ? (
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  padding={1}
                                  alignItems="center"
                                >
                                  <Typography variant="body2">
                                    {formatNumber(
                                      getActual(row.month, "EPC"),
                                    ) || 0}
                                    $
                                  </Typography>
                                  <Typography variant="body2" color="#00000099">
                                    (
                                    {(
                                      getDeviation(row.month, "EPC") * 100
                                    ).toFixed(1)}
                                    %)
                                  </Typography>
                                </Stack>
                              ) : (
                                <>
                                  {formatNumber(
                                    monthlyData[row.displayMonth]?.epc ||
                                      row.epc,
                                  )}
                                  $
                                </>
                              )}
                            </TableCell>
                            <TableCell
                              align="center"
                              onClick={() =>
                                handleCellClick(
                                  FIELD_NAMES.CPC,
                                  row.displayMonth,
                                )
                              }
                              sx={{
                                padding: "0px !important",
                                backgroundColor: isHighlighted(
                                  FIELD_NAMES.CPC,
                                  row.displayMonth,
                                )
                                  ? "#F3E5F5"
                                  : "#F5F5F5",
                                border:
                                  selectedCell?.fieldName === FIELD_NAMES.CPC &&
                                  selectedCell?.month === row.displayMonth
                                    ? "2px solid #9C27B0 !important"
                                    : "none",
                              }}
                            >
                              {getMonthIndicator(row.month) === "A" &&
                              !showOnlyForecasts ? (
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  padding={1}
                                  alignItems="center"
                                >
                                  <Typography variant="body2">
                                    {formatNumber(
                                      getActual(row.month, "CPC"),
                                    ) || 0}
                                    $
                                  </Typography>
                                  <Typography variant="body2" color="#00000099">
                                    (
                                    {(
                                      getDeviation(row.month, "CPC") * 100
                                    ).toFixed(1)}
                                    %)
                                  </Typography>
                                </Stack>
                              ) : (
                                <>
                                  {formatNumber(
                                    monthlyData[row.displayMonth]?.cpc ||
                                      row.cpc,
                                  )}
                                  $
                                </>
                              )}
                            </TableCell>
                            <TableCell
                              align="center"
                              onClick={() =>
                                handleCellClick(
                                  FIELD_NAMES.CAC,
                                  row.displayMonth,
                                )
                              }
                              sx={{
                                padding: "0px 8px",
                                backgroundColor: isHighlighted(
                                  FIELD_NAMES.CAC,
                                  row.displayMonth,
                                )
                                  ? "#F3E5F5"
                                  : "#F5F5F5",
                                border:
                                  selectedCell?.fieldName === FIELD_NAMES.CAC &&
                                  selectedCell?.month === row.displayMonth
                                    ? "2px solid #9C27B0 !important"
                                    : "none",
                              }}
                            >
                              {getMonthIndicator(row.month) === "A" &&
                              !showOnlyForecasts ? (
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  padding={1}
                                  alignItems="center"
                                >
                                  <Typography variant="body2">
                                    {formatNumber(
                                      getActual(row.month, "CAC"),
                                    ) || 0}
                                    $
                                  </Typography>
                                  <Typography variant="body2" color="#00000099">
                                    (
                                    {(
                                      getDeviation(row.month, "CAC") * 100
                                    ).toFixed(1)}
                                    %)
                                  </Typography>
                                </Stack>
                              ) : (
                                <>
                                  {formatNumber(
                                    monthlyData[row.displayMonth]?.cac ||
                                      row.CAC,
                                  )}
                                  $
                                </>
                              )}
                            </TableCell>
                            <TableCell
                              align="center"
                              onClick={() =>
                                handleCellClick(
                                  FIELD_NAMES.SPEND,
                                  row.displayMonth,
                                )
                              }
                              sx={{
                                padding: "0px !important",
                                backgroundColor: isHighlighted(
                                  FIELD_NAMES.SPEND,
                                  row.displayMonth,
                                )
                                  ? "#F3E5F5"
                                  : "#F5F5F5",
                                border:
                                  selectedCell?.fieldName ===
                                    FIELD_NAMES.SPEND &&
                                  selectedCell?.month === row.displayMonth
                                    ? "2px solid #9C27B0 !important"
                                    : "none",
                              }}
                            >
                              {getMonthIndicator(row.month) === "A" &&
                              !showOnlyForecasts ? (
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  padding={1}
                                  alignItems="center"
                                >
                                  <Typography variant="body2">
                                    {formatNumber(
                                      getActual(row.month, "spend"),
                                    ) || 0}
                                    $
                                  </Typography>
                                  <Typography variant="body2" color="#00000099">
                                    (
                                    {(
                                      getDeviation(row.month, "spend") * 100
                                    ).toFixed(1)}
                                    %)
                                  </Typography>
                                </Stack>
                              ) : (
                                <>
                                  {formatNumber(
                                    monthlyData[row.displayMonth]?.spend ||
                                      row.spend,
                                  )}
                                  $
                                </>
                              )}
                            </TableCell>
                            <TableCell
                              align="center"
                              data-tracker="131"
                              onClick={() =>
                                handleCellClick(
                                  FIELD_NAMES.AVG_RS_P,
                                  row.displayMonth,
                                )
                              }
                              sx={{
                                backgroundColor: isHighlighted(
                                  FIELD_NAMES.AVG_RS_P,
                                  row.displayMonth,
                                )
                                  ? "#F3E5F5"
                                  : isMonthPassed(row.month)
                                    ? "#F5F5F5"
                                    : "#FFFFFF",
                                padding: "0px !important",
                                position: "sticky",
                                right: computedRightOffsets[23],
                                zIndex: 2,
                                border:
                                  selectedCell?.fieldName ===
                                    FIELD_NAMES.AVG_RS_P &&
                                  selectedCell?.month === row.displayMonth
                                    ? "2px solid #9C27B0 !important"
                                    : "none",
                              }}
                            >
                              {getMonthIndicator(row.month) === "A" &&
                              !showOnlyForecasts ? (
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  padding={1}
                                  alignItems="center"
                                  justifyContent="center"
                                  sx={{ width: "100%" }}
                                >
                                  <Typography variant="body2">
                                    {formatPercentageNonRound(
                                      getActual(row.month, "avg_rs_p"),
                                    )}
                                  </Typography>
                                  <Typography variant="body2" color="#00000099">
                                    (
                                    {(
                                      getDeviation(row.month, "avg_rs_p") * 100
                                    ).toFixed(1)}
                                    %)
                                  </Typography>
                                </Stack>
                              ) : (
                                <div
                                  onClick={() => {
                                    if (!isMonthEditable(row.displayMonth))
                                      return;
                                    setEditableRsP(row.displayMonth);
                                    handleCellClick(
                                      FIELD_NAMES.AVG_RS_P,
                                      row.displayMonth,
                                    );
                                  }}
                                >
                                  <NumericFormat
                                    customInput={TextField}
                                    fullWidth
                                    focused={
                                      editableRsP === row.displayMonth &&
                                      isMonthEditable(row.displayMonth)
                                    }
                                    color="secondary"
                                    {...(editableRsP === row.displayMonth
                                      ? {
                                          defaultValue: `${Number(monthlyData[row.displayMonth]?.avgRsP * 100).toFixed(2)}`,
                                        }
                                      : {
                                          value: `${Number(monthlyData[row.displayMonth]?.avgRsP * 100).toFixed(2)}`,
                                        })}
                                    onValueChange={(values) => {
                                      onAvgRsPChange(
                                        {
                                          target: {
                                            value:
                                              (values.floatValue ?? 0) / 100,
                                          },
                                        },
                                        row.displayMonth,
                                      );
                                    }}
                                    thousandSeparator=","
                                    onBlur={() => setEditableRsP(null)}
                                    inputProps={{
                                      readOnly:
                                        editableRsP !== row.displayMonth ||
                                        !isMonthEditable(row.displayMonth),
                                      style: { textAlign: "center" },
                                    }}
                                    decimalScale={2}
                                    suffix="%"
                                    sx={{
                                      "& .MuiOutlinedInput-root": {
                                        borderRadius: "0px !important",
                                        transition:
                                          "transform 0.2s, box-shadow 0.2s",
                                        ...(editableRsP === row.displayMonth &&
                                          isMonthEditable(row.displayMonth) && {
                                            boxShadow:
                                              "0 4px 8px rgba(0,0,0,0.1)",
                                            zIndex: 1,
                                          }),
                                        "& fieldset": {
                                          border:
                                            editableRsP === row.displayMonth &&
                                            isMonthEditable(row.displayMonth)
                                              ? "unset"
                                              : "none",
                                        },
                                        "&:hover fieldset": {
                                          borderColor:
                                            editableRsP === row.displayMonth &&
                                            isMonthEditable(row.displayMonth)
                                              ? "unset"
                                              : "none",
                                        },
                                        "&.Mui-focused fieldset": {
                                          borderColor:
                                            editableRsP === row.displayMonth &&
                                            isMonthEditable(row.displayMonth)
                                              ? "unset"
                                              : "none",
                                        },
                                        "& input": {
                                          padding: "8px !important",
                                          color: "#000000DE",
                                          fontSize: "0.875rem",
                                          backgroundColor: isHighlighted(
                                            FIELD_NAMES.AVG_RS_P,
                                            row.displayMonth,
                                          )
                                            ? "#F3E5F5"
                                            : "#FFFFFF",
                                          cursor: isMonthEditable(
                                            row.displayMonth,
                                          )
                                            ? editableRsP === row.displayMonth
                                              ? "text"
                                              : "pointer"
                                            : "pointer",
                                          transition: "background-color 0.2s",
                                        },
                                      },
                                    }}
                                  />
                                </div>
                              )}
                            </TableCell>
                            <TableCell
                              align="center"
                              data-tracker="131"
                              onClick={() =>
                                handleCellClick(
                                  FIELD_NAMES.CHURN_RATE,
                                  row.displayMonth,
                                )
                              }
                              sx={{
                                backgroundColor: isHighlighted(
                                  FIELD_NAMES.CHURN_RATE,
                                  row.displayMonth,
                                )
                                  ? "#F3E5F5"
                                  : isMonthPassed(row.month)
                                    ? "#F5F5F5"
                                    : "#FFFFFF",
                                padding: "0px !important",
                                position: "sticky",
                                right: computedRightOffsets[24],
                                zIndex: 2,
                                border:
                                  selectedCell?.fieldName ===
                                    FIELD_NAMES.CHURN_RATE &&
                                  selectedCell?.month === row.displayMonth
                                    ? "2px solid #9C27B0 !important"
                                    : "none",
                              }}
                            >
                              {getMonthIndicator(row.month) === "A" &&
                              !showOnlyForecasts ? (
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  padding={1}
                                  alignItems="center"
                                >
                                  <Typography variant="body2">
                                    {formatPercentageNonRound(
                                      getActual(row.month, "churn_rate"),
                                    )}
                                  </Typography>
                                  <Typography variant="body2" color="#00000099">
                                    (
                                    {(
                                      getDeviation(row.month, "churn_rate") *
                                      100
                                    ).toFixed(1)}
                                    %)
                                  </Typography>
                                </Stack>
                              ) : (
                                <div
                                  onClick={() => {
                                    if (!isMonthEditable(row.displayMonth))
                                      return;
                                    setEditableChurnRate(row.displayMonth);
                                    handleCellClick(
                                      FIELD_NAMES.CHURN_RATE,
                                      row.displayMonth,
                                    );
                                  }}
                                >
                                  <NumericFormat
                                    customInput={TextField}
                                    fullWidth
                                    focused={
                                      editableChurnRate === row.displayMonth &&
                                      isMonthEditable(row.displayMonth)
                                    }
                                    color="secondary"
                                    {...(editableChurnRate === row.displayMonth
                                      ? {
                                          defaultValue: `${Number(monthlyData[row.displayMonth]?.churnRate * 100).toFixed(2)}`,
                                        }
                                      : {
                                          value: `${Number(monthlyData[row.displayMonth]?.churnRate * 100).toFixed(2)}`,
                                        })}
                                    onValueChange={(values) => {
                                      onChurnRateChange(
                                        {
                                          target: {
                                            value:
                                              (values.floatValue ?? 0) / 100,
                                          },
                                        },
                                        row.displayMonth,
                                      );
                                    }}
                                    thousandSeparator=","
                                    onBlur={() => setEditableChurnRate(null)}
                                    inputProps={{
                                      readOnly:
                                        editableChurnRate !==
                                          row.displayMonth ||
                                        !isMonthEditable(row.displayMonth),
                                      style: { textAlign: "center" },
                                    }}
                                    decimalScale={2}
                                    suffix="%"
                                    sx={{
                                      "& .MuiOutlinedInput-root": {
                                        borderRadius: "0px !important",
                                        transition:
                                          "transform 0.2s, box-shadow 0.2s",
                                        ...(editableChurnRate ===
                                          row.displayMonth &&
                                          isMonthEditable(row.displayMonth) && {
                                            boxShadow:
                                              "0 4px 8px rgba(0,0,0,0.1)",
                                            zIndex: 1,
                                          }),
                                        "& fieldset": {
                                          border:
                                            editableChurnRate ===
                                              row.displayMonth &&
                                            isMonthEditable(row.displayMonth)
                                              ? "unset"
                                              : "none",
                                        },
                                        "&:hover fieldset": {
                                          borderColor:
                                            editableChurnRate ===
                                              row.displayMonth &&
                                            isMonthEditable(row.displayMonth)
                                              ? "unset"
                                              : "none",
                                        },
                                        "&.Mui-focused fieldset": {
                                          borderColor:
                                            editableChurnRate ===
                                              row.displayMonth &&
                                            isMonthEditable(row.displayMonth)
                                              ? "unset"
                                              : "none",
                                        },
                                        "& input": {
                                          padding: "8px !important",
                                          color: "#000000DE",
                                          fontSize: "0.875rem",
                                          backgroundColor: isHighlighted(
                                            FIELD_NAMES.CHURN_RATE,
                                            row.displayMonth,
                                          )
                                            ? "#F3E5F5"
                                            : "#FFFFFF",
                                          cursor: isMonthEditable(
                                            row.displayMonth,
                                          )
                                            ? editableChurnRate ===
                                              row.displayMonth
                                              ? "text"
                                              : "pointer"
                                            : "pointer",
                                          transition: "background-color 0.2s",
                                        },
                                      },
                                    }}
                                  />
                                </div>
                              )}
                            </TableCell>
                            <TableCell
                              align="center"
                              data-tracker="131"
                              onClick={() =>
                                handleCellClick(
                                  FIELD_NAMES.RS_BR_NG,
                                  row.displayMonth,
                                )
                              }
                              sx={{
                                backgroundColor: isHighlighted(
                                  FIELD_NAMES.RS_BR_NG,
                                  row.displayMonth,
                                )
                                  ? "#F3E5F5"
                                  : isMonthPassed(row.month)
                                    ? "#F5F5F5"
                                    : "#FFFFFF",
                                padding: "0px !important",
                                position: "sticky",
                                right: computedRightOffsets[25],
                                zIndex: 2,
                                border:
                                  selectedCell?.fieldName ===
                                    FIELD_NAMES.RS_BR_NG &&
                                  selectedCell?.month === row.displayMonth
                                    ? "2px solid #9C27B0 !important"
                                    : "none",
                              }}
                            >
                              {getMonthIndicator(row.month) === "A" &&
                              !showOnlyForecasts ? (
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  padding={1}
                                  alignItems="center"
                                >
                                  <Typography variant="body2">
                                    {formatPercentageNonRound(
                                      getActual(row.month, "rs_br_ng"),
                                    )}
                                  </Typography>
                                  <Typography variant="body2" color="#00000099">
                                    (
                                    {(
                                      getDeviation(row.month, "rs_br_ng") * 100
                                    ).toFixed(1)}
                                    %)
                                  </Typography>
                                </Stack>
                              ) : (
                                <div
                                  onClick={() => {
                                    if (!isMonthEditable(row.displayMonth))
                                      return;
                                    setEditableRsBrNg(row.displayMonth);
                                    handleCellClick(
                                      FIELD_NAMES.RS_BR_NG,
                                      row.displayMonth,
                                    );
                                  }}
                                >
                                  <NumericFormat
                                    customInput={TextField}
                                    fullWidth
                                    focused={
                                      editableRsBrNg === row.displayMonth &&
                                      isMonthEditable(row.displayMonth)
                                    }
                                    color="secondary"
                                    {...(editableRsBrNg === row.displayMonth
                                      ? {
                                          defaultValue: `${Number(monthlyData[row.displayMonth]?.rsBrToRsActiveRatio * 100).toFixed(2)}`,
                                        }
                                      : {
                                          value: `${Number(monthlyData[row.displayMonth]?.rsBrToRsActiveRatio * 100).toFixed(2)}`,
                                        })}
                                    onValueChange={(values) => {
                                      onRsBrToRsActiveRatio(
                                        {
                                          target: {
                                            value:
                                              (values.floatValue ?? 0) / 100,
                                          },
                                        },
                                        row.displayMonth,
                                      );
                                    }}
                                    thousandSeparator=","
                                    onBlur={() => setEditableRsBrNg(null)}
                                    inputProps={{
                                      readOnly:
                                        editableRsBrNg !== row.displayMonth ||
                                        !isMonthEditable(row.displayMonth),
                                      style: { textAlign: "center" },
                                    }}
                                    decimalScale={2}
                                    suffix="%"
                                    sx={{
                                      "& .MuiOutlinedInput-root": {
                                        borderRadius: "0px !important",
                                        transition:
                                          "transform 0.2s, box-shadow 0.2s",
                                        ...(editableRsBrNg ===
                                          row.displayMonth &&
                                          isMonthEditable(row.displayMonth) && {
                                            boxShadow:
                                              "0 4px 8px rgba(0,0,0,0.1)",
                                            zIndex: 1,
                                          }),
                                        "& fieldset": {
                                          border:
                                            editableRsBrNg ===
                                              row.displayMonth &&
                                            isMonthEditable(row.displayMonth)
                                              ? "unset"
                                              : "none",
                                        },
                                        "&:hover fieldset": {
                                          borderColor:
                                            editableRsBrNg ===
                                              row.displayMonth &&
                                            isMonthEditable(row.displayMonth)
                                              ? "unset"
                                              : "none",
                                        },
                                        "&.Mui-focused fieldset": {
                                          borderColor:
                                            editableRsBrNg ===
                                              row.displayMonth &&
                                            isMonthEditable(row.displayMonth)
                                              ? "unset"
                                              : "none",
                                        },
                                        "& input": {
                                          padding: "8px !important",
                                          color: "#000000DE",
                                          fontSize: "0.875rem",
                                          backgroundColor: isHighlighted(
                                            FIELD_NAMES.RS_BR_NG,
                                            row.displayMonth,
                                          )
                                            ? "#F3E5F5"
                                            : "#FFFFFF",
                                          cursor: isMonthEditable(
                                            row.displayMonth,
                                          )
                                            ? editableRsBrNg ===
                                              row.displayMonth
                                              ? "text"
                                              : "pointer"
                                            : "pointer",
                                          transition: "background-color 0.2s",
                                        },
                                      },
                                    }}
                                  />
                                </div>
                              )}
                            </TableCell>
                            <TableCell
                              align="center"
                              data-tracker="131"
                              onClick={() =>
                                handleCellClick(
                                  FIELD_NAMES.ACQUIRED_TO_FTD,
                                  row.displayMonth,
                                )
                              }
                              sx={{
                                backgroundColor: isHighlighted(
                                  FIELD_NAMES.ACQUIRED_TO_FTD,
                                  row.displayMonth,
                                )
                                  ? "#F3E5F5"
                                  : isMonthPassed(row.month)
                                    ? "#F5F5F5"
                                    : "#FFFFFF",
                                padding: "0px !important",
                                position: "sticky",
                                right: computedRightOffsets[26],
                                zIndex: 2,
                                border:
                                  selectedCell?.fieldName ===
                                    FIELD_NAMES.ACQUIRED_TO_FTD &&
                                  selectedCell?.month === row.displayMonth
                                    ? "2px solid #9C27B0 !important"
                                    : "none",
                              }}
                            >
                              {getMonthIndicator(row.month) === "A" &&
                              !showOnlyForecasts ? (
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  padding={1}
                                  alignItems="center"
                                >
                                  <Typography variant="body2">
                                    {formatPercentageNonRound(
                                      getActual(
                                        row.month,
                                        "Ratio_between_acquired_to_ftd",
                                      ),
                                    )}
                                  </Typography>
                                  <Typography variant="body2" color="#00000099">
                                    (
                                    {(
                                      getDeviation(
                                        row.month,
                                        "Ratio_between_acquired_to_ftd",
                                      ) * 100
                                    ).toFixed(1)}
                                    %)
                                  </Typography>
                                </Stack>
                              ) : (
                                <div
                                  onClick={() => {
                                    if (!isMonthEditable(row.displayMonth))
                                      return;
                                    setEditableAcquiredToFtd(row.displayMonth);
                                    handleCellClick(
                                      FIELD_NAMES.ACQUIRED_TO_FTD,
                                      row.displayMonth,
                                    );
                                  }}
                                >
                                  <NumericFormat
                                    customInput={TextField}
                                    fullWidth
                                    focused={
                                      editableAcquiredToFtd ===
                                        row.displayMonth &&
                                      isMonthEditable(row.displayMonth)
                                    }
                                    color="secondary"
                                    {...(editableAcquiredToFtd ===
                                    row.displayMonth
                                      ? {
                                          defaultValue: `${Number(monthlyData[row.displayMonth]?.ratioBetweenAcquiredToFtd * 100).toFixed(2)}`,
                                        }
                                      : {
                                          value: `${Number(monthlyData[row.displayMonth]?.ratioBetweenAcquiredToFtd * 100).toFixed(2)}`,
                                        })}
                                    onValueChange={(values) => {
                                      onRatioAcquiredToFtdChange(
                                        {
                                          target: {
                                            value:
                                              (values.floatValue ?? 0) / 100,
                                          },
                                        },
                                        row.displayMonth,
                                      );
                                    }}
                                    thousandSeparator=","
                                    onBlur={() =>
                                      setEditableAcquiredToFtd(null)
                                    }
                                    inputProps={{
                                      readOnly:
                                        editableAcquiredToFtd !==
                                          row.displayMonth ||
                                        !isMonthEditable(row.displayMonth),
                                      style: { textAlign: "center" },
                                    }}
                                    decimalScale={2}
                                    suffix="%"
                                    sx={{
                                      "& .MuiOutlinedInput-root": {
                                        borderRadius: "0px !important",
                                        transition:
                                          "transform 0.2s, box-shadow 0.2s",
                                        ...(editableAcquiredToFtd ===
                                          row.displayMonth &&
                                          isMonthEditable(row.displayMonth) && {
                                            boxShadow:
                                              "0 4px 8px rgba(0,0,0,0.1)",
                                            zIndex: 1,
                                          }),
                                        "& fieldset": {
                                          border:
                                            editableAcquiredToFtd ===
                                              row.displayMonth &&
                                            isMonthEditable(row.displayMonth)
                                              ? "unset"
                                              : "none",
                                        },
                                        "&:hover fieldset": {
                                          borderColor:
                                            editableAcquiredToFtd ===
                                              row.displayMonth &&
                                            isMonthEditable(row.displayMonth)
                                              ? "unset"
                                              : "none",
                                        },
                                        "&.Mui-focused fieldset": {
                                          borderColor:
                                            editableAcquiredToFtd ===
                                              row.displayMonth &&
                                            isMonthEditable(row.displayMonth)
                                              ? "unset"
                                              : "none",
                                        },
                                        "& input": {
                                          padding: "8px !important",
                                          color: "#000000DE",
                                          fontSize: "0.875rem",
                                          backgroundColor: isHighlighted(
                                            FIELD_NAMES.ACQUIRED_TO_FTD,
                                            row.displayMonth,
                                          )
                                            ? "#F3E5F5"
                                            : "#FFFFFF",
                                          cursor: isMonthEditable(
                                            row.displayMonth,
                                          )
                                            ? editableAcquiredToFtd ===
                                              row.displayMonth
                                              ? "text"
                                              : "pointer"
                                            : "pointer",
                                          transition: "background-color 0.2s",
                                        },
                                      },
                                    }}
                                  />
                                </div>
                              )}
                            </TableCell>
                          </TableRow>
                          {isQuarterEnd && quarterTotals && (
                            <TableRow
                              sx={{
                                backgroundColor: "transparent",
                                "& > *": {
                                  borderBottom: "2px solid #E7E7E7 !important",
                                },
                                height: 35,
                              }}
                            >
                              <QuarterSummaryCell
                                value={`Q${Math.ceil((monthNames.indexOf(row.displayMonth) + 1) / 3)} Total`}
                                bgColor="#F5F5F5"
                                isSticky
                                leftOffset={computedLeftOffsets[0] ?? 0}
                              />
                              <QuarterSummaryCell
                                bgColor="#F5F5F5"
                                isSticky
                                leftOffset={computedLeftOffsets[1]}
                              />
                              <QuarterSummaryCell
                                value={formatNumber(quarterTotals.ftd)}
                                bgColor="#F5F5F5"
                                isSticky
                                leftOffset={computedLeftOffsets[2]}
                              />
                              <QuarterSummaryCell
                                bgColor="#F5F5F5"
                                isSticky
                                leftOffset={computedLeftOffsets[3]}
                              />
                              <QuarterSummaryCell
                                bgColor="#F5F5F5"
                                isSticky
                                leftOffset={computedLeftOffsets[4]}
                              />
                              <QuarterSummaryCell
                                bgColor="#F5F5F5"
                                isSticky
                                leftOffset={computedLeftOffsets[5]}
                              />
                              <QuarterSummaryCell
                                value={`${formatNumber(quarterTotals.profit)}$`}
                                bgColor="#F5F5F5"
                                isSticky
                                leftOffset={computedLeftOffsets[6]}
                                hasBorder
                              />
                              <QuarterSummaryCell bgColor="#F5F5F5" />
                              <QuarterSummaryCell bgColor="#F5F5F5" />
                              <QuarterSummaryCell bgColor="#F5F5F5" />
                              <QuarterSummaryCell bgColor="#F5F5F5" />
                              <QuarterSummaryCell
                                value={`${formatNumber(quarterTotals.estNetRev)}$`}
                                bgColor="#F5F5F5"
                              />
                              <QuarterSummaryCell
                                value={`${formatNumber(quarterTotals.estRs)}$`}
                                bgColor="#F5F5F5"
                              />
                              <QuarterSummaryCell bgColor="#F5F5F5" />
                              <QuarterSummaryCell
                                value={`${formatNumber(quarterTotals.estCpaIncome)}$`}
                                bgColor="#F5F5F5"
                              />
                              <QuarterSummaryCell
                                value={`${formatNumber(quarterTotals.totalIncome)}$`}
                                bgColor="#F5F5F5"
                              />
                              <QuarterSummaryCell
                                value={`${formatNumber(quarterTotals.clicks)}`}
                                bgColor="#F5F5F5"
                              />
                              <QuarterSummaryCell bgColor="#F5F5F5" />
                              <QuarterSummaryCell bgColor="#F5F5F5" />
                              <QuarterSummaryCell bgColor="#F5F5F5" />
                              <QuarterSummaryCell bgColor="#F5F5F5" />
                              <QuarterSummaryCell bgColor="#F5F5F5" />
                              <QuarterSummaryCell
                                value={`${formatNumber(quarterTotals.spend)}$`}
                                bgColor="#F5F5F5"
                              />
                              <QuarterSummaryCell
                                bgColor="#F5F5F5"
                                isSticky
                                rightOffset={computedRightOffsets[23]}
                              />
                              <QuarterSummaryCell
                                bgColor="#F5F5F5"
                                isSticky
                                rightOffset={computedRightOffsets[24]}
                              />
                              <QuarterSummaryCell
                                bgColor="#F5F5F5"
                                isSticky
                                rightOffset={computedRightOffsets[25]}
                              />
                              <QuarterSummaryCell
                                bgColor="#F5F5F5"
                                isSticky
                                rightOffset={computedRightOffsets[26]}
                              />
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                    {(() => {
                      const allMonths = Object.values(monthlyData).filter(
                        (m: any) => m.displayMonth !== "placeholder",
                      );

                      const yearTotals = {
                        ftd: allMonths.reduce(
                          (sum: number, m: any) => sum + (m.ftd || 0),
                          0,
                        ),
                        profit: allMonths.reduce(
                          (sum: number, m: any) => sum + (m.profit || 0),
                          0,
                        ),
                        growth: allMonths.reduce(
                          (sum: number, m: any) => sum + (m.growth || 0),
                          0,
                        ),
                        estNetRev: allMonths.reduce(
                          (sum: number, m: any) => sum + (m.estNetRev || 0),
                          0,
                        ),
                        estRs: allMonths.reduce(
                          (sum: number, m: any) => sum + (m.estRs || 0),
                          0,
                        ),
                        estCpaIncome: allMonths.reduce(
                          (sum: number, m: any) => sum + (m.estCpaIncome || 0),
                          0,
                        ),
                        totalIncome: allMonths.reduce(
                          (sum: number, m: any) => sum + (m.totalIncome || 0),
                          0,
                        ),
                        spend: allMonths.reduce(
                          (sum: number, m: any) => sum + (m.spend || 0),
                          0,
                        ),
                        cac: allMonths.reduce(
                          (sum: number, m: any) => sum + (m.cac || 0),
                          0,
                        ),
                        clicks: allMonths.reduce(
                          (sum: number, m: any) => sum + (m.clicks || 0),
                          0,
                        ),
                      };
                      return (
                        <TableRow
                          sx={{
                            backgroundColor: "#FFF3E0",
                            height: 35,
                          }}
                        >
                          <QuarterSummaryCell
                            value={`${selectedYear} Total`}
                            bgColor="#F5F5F5"
                            fontWeight={500}
                            isSticky
                            leftOffset={computedLeftOffsets[0] ?? 0}
                          />
                          <QuarterSummaryCell
                            bgColor="#F5F5F5"
                            isSticky
                            leftOffset={computedLeftOffsets[1]}
                          />
                          <QuarterSummaryCell
                            value={formatNumber(yearTotals.ftd)}
                            bgColor="#F5F5F5"
                            fontWeight={500}
                            isSticky
                            leftOffset={computedLeftOffsets[2]}
                          />
                          <QuarterSummaryCell
                            bgColor="#F5F5F5"
                            isSticky
                            leftOffset={computedLeftOffsets[3]}
                          />
                          <QuarterSummaryCell
                            bgColor="#F5F5F5"
                            isSticky
                            leftOffset={computedLeftOffsets[4]}
                          />
                          <QuarterSummaryCell
                            bgColor="#F5F5F5"
                            isSticky
                            leftOffset={computedLeftOffsets[5]}
                          />
                          <QuarterSummaryCell
                            value={`${formatNumber(yearTotals.profit)}$`}
                            bgColor="#F5F5F5"
                            fontWeight={500}
                            isSticky
                            leftOffset={computedLeftOffsets[6]}
                            hasBorder
                          />
                          <QuarterSummaryCell bgColor="#F5F5F5" />
                          <QuarterSummaryCell bgColor="#F5F5F5" />
                          <QuarterSummaryCell bgColor="#F5F5F5" />
                          <QuarterSummaryCell
                            bgColor="#F5F5F5"
                            fontWeight={500}
                          />
                          <QuarterSummaryCell
                            value={`${formatNumber(yearTotals.estNetRev)}$`}
                            bgColor="#F5F5F5"
                            fontWeight={500}
                          />
                          <QuarterSummaryCell
                            value={`${formatNumber(yearTotals.estRs)}$`}
                            bgColor="#F5F5F5"
                            fontWeight={500}
                          />
                          <QuarterSummaryCell bgColor="#F5F5F5" />
                          <QuarterSummaryCell
                            value={`${formatNumber(yearTotals.estCpaIncome)}$`}
                            bgColor="#F5F5F5"
                            fontWeight={500}
                          />
                          <QuarterSummaryCell
                            value={`${formatNumber(yearTotals.totalIncome)}$`}
                            bgColor="#F5F5F5"
                            fontWeight={500}
                          />
                          <QuarterSummaryCell
                            value={`${formatNumber(yearTotals.clicks)}`}
                            bgColor="#F5F5F5"
                          />
                          <QuarterSummaryCell bgColor="#F5F5F5" />
                          <QuarterSummaryCell bgColor="#F5F5F5" />
                          <QuarterSummaryCell bgColor="#F5F5F5" />
                          <QuarterSummaryCell bgColor="#F5F5F5" />
                          <QuarterSummaryCell
                            bgColor="#F5F5F5"
                            fontWeight={500}
                          />
                          <QuarterSummaryCell
                            value={`${formatNumber(yearTotals.spend)}$`}
                            bgColor="#F5F5F5"
                            fontWeight={500}
                          />
                          <QuarterSummaryCell
                            bgColor="#F5F5F5"
                            isSticky
                            rightOffset={computedRightOffsets[23]}
                          />
                          <QuarterSummaryCell
                            bgColor="#F5F5F5"
                            isSticky
                            rightOffset={computedRightOffsets[24]}
                          />
                          <QuarterSummaryCell
                            bgColor="#F5F5F5"
                            isSticky
                            rightOffset={computedRightOffsets[25]}
                          />
                          <QuarterSummaryCell
                            bgColor="#F5F5F5"
                            isSticky
                            rightOffset={computedRightOffsets[26]}
                          />
                        </TableRow>
                      );
                    })()}
                  </TableBody>
                </Table>
                <Stack
                  spacing={1}
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                ></Stack>
              </TableContainer>
              <TableWithOffsetScrollbar
                fixedWidth={fixedColumnsWidth}
                scrollRef={tableScrollRef}
              />
            </>
          </Paper>
          <MetricsByMonthDialogue
            open={openMetrics}
            handleClose={handleClose}
            selectedSiteFromParent={selectedSite}
          />
        </>
        ) : (
          <ForecastSkeleton />
        )
      ) : (
        <ForecastSkeleton />
      )}
    </>
  );
};
